import React, { useEffect, useState, useMemo } from "react";
import {
  Alert, Button, Card, Divider, Form, Input, InputNumber, Space, Tag, Select, Checkbox,
  message, Tabs, Tooltip, Typography, Spin, Breadcrumb, Modal, Upload, Progress, ColorPicker, Switch
} from "antd";
import { FaCheck, FaUndo, FaArchive, FaArrowLeft, FaEye, FaEdit, FaTimes } from "react-icons/fa";
import { SettingOutlined, ThunderboltOutlined, UploadOutlined, GlobalOutlined, CloseOutlined, TagOutlined, BankOutlined, PlusOutlined } from "@ant-design/icons";
import { useParams, useNavigate, Link } from "react-router-dom";
import axiosHttp from "../../appRedux/services/axiosCommon";
import Page from "../../components/Common/Page";
import AIGeneratorConfig from "../../components/Common/AIGeneratorConfig";
import ExaSearchConfig from "../../components/Common/ExaSearchConfig";
import TipTapEditor from "../../components/Common/TipTapEditor";
import DebouncedCourseSelect from "../../components/Common/DebouncedCourseSelect";

const MentorSelect = ({ value, onChange, placeholder = "Search & select mentor from database..." }) => {
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMentors = async () => {
      setLoading(true);
      try {
        const res = await axiosHttp.get("/v1/admin/mentors", { params: { size: 100 } });
        const list = res.data?.results?.data || res.data?.data?.data || res.data?.data || res.data?.results || [];
        setMentors(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Failed to load mentors", e);
      } finally {
        setLoading(false);
      }
    };
    fetchMentors();
  }, []);

  return (
    <Select
      showSearch
      allowClear
      loading={loading}
      placeholder={placeholder}
      optionFilterProp="label"
      value={value}
      onChange={(selectedId) => {
        const found = mentors.find(m => String(m._id) === String(selectedId));
        if (onChange) {
          onChange(selectedId, found);
        }
      }}
      options={mentors.map(m => ({
        label: `${m.name} (${m.designation ? `${m.designation}, ` : ''}${m.organization || 'Independent'})`,
        value: m._id,
        mentor: m
      }))}
      style={{ width: '100%' }}
    />
  );
};

const PALETTE_COLORS = ["#0284c7", "#059669", "#7c3aed", "#d97706", "#db2777", "#0891b2", "#ca8a04"];

const ICON_MAPPINGS = {
  briefcase: "work",
  shield_alt: "security",
  shield: "security",
  check: "check_circle",
  times: "cancel",
  cog: "settings",
  cogs: "settings",
  user: "person",
  users: "group",
  certificate: "workspace_premium",
  award: "military_tech",
  lightbulb: "lightbulb",
  book: "menu_book",
  desktop: "desktop_windows",
  server: "dns",
  database: "storage",
  globe: "public",
  rocket: "rocket_launch",
  fire: "local_fire_department",
};

const parseTagData = (tagObj, index = 0) => {
  let name = typeof tagObj === 'object' && tagObj !== null ? (tagObj.name || tagObj.title || tagObj.label || '') : String(tagObj || '');
  let icon = typeof tagObj === 'object' && tagObj !== null ? tagObj.icon : null;
  let color = typeof tagObj === 'object' && tagObj !== null ? tagObj.color : null;

  if (typeof name === 'string' && name.trim()) {
    // 1. Check for legacy class prefixes like "la la-briefcase enterprise", "fa fa-terminal backend"
    const prefixMatch = name.match(/^(la\s+la-[a-z0-9-]+|fa\s+fa-[a-z0-9-]+|las\s+la-[a-z0-9-]+|fas\s+fa-[a-z0-9-]+|la-[a-z0-9-]+|fa-[a-z0-9-]+)\s+(.+)$/i);
    if (prefixMatch) {
      if (!icon) icon = prefixMatch[1];
      name = prefixMatch[2];
    } else {
      // 2. Check for single-word uppercase/lowercase icon names at the start of name like "BRIEFCASE enterprise"
      const wordMatch = name.match(/^([A-Z0-9_-]{3,20})\s+(.+)$/i);
      if (wordMatch) {
        const potentialIcon = wordMatch[1].toLowerCase().replace(/_/g, '-');
        if (ICON_MAPPINGS[potentialIcon] || ["briefcase", "terminal", "shield", "cogs", "cog", "user", "users", "code", "laptop", "server", "database", "globe", "rocket", "fire", "bolt", "star", "award", "certificate", "book", "tag", "lock", "key"].includes(potentialIcon)) {
          if (!icon) icon = potentialIcon;
          name = wordMatch[2];
        }
      }
    }
  }

  let resolvedColor = color && typeof color === 'string' && color.trim() ? color.trim() : PALETTE_COLORS[Math.abs(index) % PALETTE_COLORS.length];

  return { name: name || "tag", icon, color: resolvedColor };
};

const renderTagIcon = (tagObj, index = 0) => {
  const { icon, color } = parseTagData(tagObj, index);
  if (icon && typeof icon === "string") {
    if (icon.startsWith("http://") || icon.startsWith("https://") || icon.startsWith("/")) {
      return (
        <img
          src={icon}
          alt=""
          style={{ width: 14, height: 14, objectFit: "contain", verticalAlign: "middle", borderRadius: 2, marginRight: 4 }}
        />
      );
    }

    let matSymbol = icon.toLowerCase().trim();
    if (matSymbol.includes("la-") || matSymbol.includes("fa-")) {
      const iconName = matSymbol.substring(matSymbol.indexOf("-") + 1).replace(/-/g, "_");
      matSymbol = ICON_MAPPINGS[iconName] || iconName;
    } else {
      const cleanKey = matSymbol.replace(/-/g, "_");
      matSymbol = ICON_MAPPINGS[cleanKey] || matSymbol;
    }

    return (
      <span
        className="material-symbols-outlined"
        style={{ fontSize: 15, verticalAlign: "middle", marginRight: 4, color, lineHeight: 1 }}
      >
        {matSymbol}
      </span>
    );
  }
  return <TagOutlined style={{ fontSize: 12, color, marginRight: 4 }} />;
};

const CertificationCardsPreview = ({ pageForm, itCertCourses }) => {
  const assignedIds = pageForm.getFieldValue("certificationCourses") || [];
  const [courseMap, setCourseMap] = useState({});

  useEffect(() => {
    if (!Array.isArray(assignedIds) || assignedIds.length === 0) return;
    
    const missingIds = assignedIds.filter(id => {
      if (courseMap[id]) return false;
      const foundInPool = (itCertCourses || []).some(c => c._id === id || c.id === id);
      return !foundInPool;
    });

    if (missingIds.length > 0) {
      const promises = missingIds.map(id => axiosHttp.get(`/v1/course/${id}`).catch(() => null));
      Promise.all(promises).then(results => {
        const newMap = { ...courseMap };
        results.forEach(res => {
          const c = res?.data?.results || res?.data?.data || res?.data;
          if (c && (c._id || c.id)) {
            newMap[c._id || c.id] = c;
          }
        });
        setCourseMap(newMap);
      });
    }
  }, [assignedIds, itCertCourses]);

  if (!Array.isArray(assignedIds) || assignedIds.length === 0) return null;

  const assignedCourses = assignedIds.map(id => {
    const fromPool = (itCertCourses || []).find(c => c._id === id || c.id === id || c.title === id);
    return fromPool || courseMap[id] || { _id: id, title: id };
  });

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: '#475569', marginBottom: 12 }}>
        Assigned Certification Cards ({assignedCourses.length}):
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
        {assignedCourses.map((course) => {
          const cId = course._id || course.id || course.title;
          const provider = course.provider || course.merchant || course.platform || "Certification";
          const price = course.price_detail?.price || course.price || "Free / Paid";
          const rating = course.rating || course.rating_detail?.rating || "4.8";

          return (
            <Card
              key={cId}
              size="small"
              style={{ borderRadius: 8, borderColor: "#cbd5e1", background: "#ffffff" }}
              bodyStyle={{ padding: 12 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#6366f1", background: "#eef2ff", padding: "2px 6px", borderRadius: 4 }}>
                  {provider}
                </span>
                <span style={{ fontSize: 11, color: "#eab308", fontWeight: 600 }}>★ {rating}</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 6, lineClamp: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: 38 }}>
                {course.title || course.name || "Certification Course"}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 6, borderTop: "1px solid #f1f5f9" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{price}</span>
                <Tag color="green" style={{ margin: 0, fontSize: 10 }}>Assigned</Tag>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};


const SkillPageBuilder = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [pageForm] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [topics, setTopics] = useState([]);
  const [itCertCourses, setItCertCourses] = useState([]);
  const [loadingItCertCourses, setLoadingItCertCourses] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [isDraftEditMode, setIsDraftEditMode] = useState(false);
  const isEditable = editingPage?.status === "draft" || isDraftEditMode;

  const fetchAvailableTags = async () => {
    try {
      const response = await axiosHttp.get("/v3/tags?scope=skill");
      if (response.data?.data) {
        setAvailableTags(response.data.data);
      } else if (Array.isArray(response.data)) {
        setAvailableTags(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch available skill tags", error);
    }
  };

  const ensureTagsCreated = async (rawTags) => {
    if (!Array.isArray(rawTags)) return [];
    const processedIds = [];
    let updatedList = [...availableTags];
    let listChanged = false;

    for (const item of rawTags) {
      if (!item) continue;
      const existing = updatedList.find(
        t => String(t._id) === String(item) || t.name?.toLowerCase() === String(item).toLowerCase()
      );

      if (existing) {
        processedIds.push(existing._id);
      } else {
        try {
          const createRes = await axiosHttp.post("/v3/admin/tags", {
            name: String(item).trim(),
            scope: "skill"
          });
          const newTag = createRes.data?.data;
          if (newTag && newTag._id) {
            processedIds.push(newTag._id);
            updatedList.push(newTag);
            listChanged = true;
          }
        } catch (err) {
          console.error("Failed to create new tag:", item, err);
        }
      }
    }

    if (listChanged) {
      setAvailableTags(updatedList);
    }
    return processedIds;
  };

  const [courseCollections, setCourseCollections] = useState([]);

  const fetchCourseCollections = async () => {
    try {
      const response = await axiosHttp.get("/v3/admin/course-collections?limit=100");
      const list = response.data?.data || [];
      setCourseCollections(list);
    } catch (e) {
      console.warn("Failed to fetch course collections", e);
    }
  };

  const fetchItCertCourses = async () => {
    setLoadingItCertCourses(true);
    try {
      const response = await axiosHttp.get("/v2/courses", {
        params: { param: "categories", value: "IT Certifications", size: 100, page: 0, "sort-by": "rating:desc" },
        headers: { 'Cache-Control': 'no-cache' }
      });
      const resData = response.data;
      const raw = Array.isArray(resData) 
        ? resData 
        : (resData?.results?.courses?.data || resData?.results?.courses || resData?.data || resData?.results || resData?.courses || resData?.items || []);
      let courses = Array.isArray(raw) ? raw : [];
      setItCertCourses(courses);
    } catch (error) {
      console.error("Failed to fetch IT Certification courses:", error);
    } finally {
      setLoadingItCertCourses(false);
    }
  };

  const handleAutoMatchCertifications = async () => {
    const skillName = pageForm.getFieldValue("name") || editingPage?.name || "";
    if (!skillName) {
      return message.warning("A skill name is required to auto-match certification courses.");
    }
    
    setLoadingItCertCourses(true);
    let candidateCourses = [];

    try {
      // 1. Directly query API with skillName for targeted database search
      const response = await axiosHttp.get("/v2/courses", {
        params: { q: skillName, title: skillName, size: 50, page: 0, "sort-by": "rating:desc" },
        headers: { 'Cache-Control': 'no-cache' }
      });
      const resData = response.data;
      const raw = Array.isArray(resData) 
        ? resData 
        : (resData?.results?.courses?.data || resData?.results?.courses || resData?.data || resData?.results || resData?.courses || resData?.items || []);
      candidateCourses = Array.isArray(raw) ? raw : [];
    } catch (e) {
      console.error("Auto match courses fetch error:", e);
    } finally {
      setLoadingItCertCourses(false);
    }

    // Fall back to pre-fetched pool if direct search returned empty
    if (candidateCourses.length === 0 && Array.isArray(itCertCourses) && itCertCourses.length > 0) {
      candidateCourses = itCertCourses;
    }

    if (candidateCourses.length === 0) {
      return message.error(`No IT certification courses found for "${skillName}".`);
    }

    const keywords = skillName.toLowerCase().split(/\s+/).filter(k => k.length >= 2);
    const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const scoredMatched = candidateCourses
      .map(course => {
        const title = course.title || course.name || "";
        const desc = course.headline || course.description || "";
        let score = 0;
        keywords.forEach(kw => {
          const regex = new RegExp(`\\b${escapeRegex(kw)}\\b`, 'i');
          if (regex.test(title)) score += 5;
          else if (regex.test(desc)) score += 1;
        });
        return { course, score };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score);

    if (scoredMatched.length === 0) {
      return message.warning(`No IT certification courses found specifically matching "${skillName}".`);
    }

    const selectedList = scoredMatched.slice(0, 4).map(item => item.course._id || item.course.id || item.course.title);
    const currentList = pageForm.getFieldValue("certificationCourses") || [];
    const merged = Array.from(new Set([...currentList, ...selectedList]));
    
    pageForm.setFieldsValue({ certificationCourses: merged });
    message.success(`Auto-matched ${selectedList.length} relevant IT certification courses for "${skillName}"!`);
  };

  const [loadingDomainCerts, setLoadingDomainCerts] = useState({});

  const handleAutoMatchDomainCertifications = async (index) => {
    const skillName = pageForm.getFieldValue("name") || editingPage?.name || "";
    const outcomes = pageForm.getFieldValue("careerOutcomes") || [];
    const outcome = outcomes[index];
    if (!outcome || !outcome.role) {
      message.warning("A role name is required to auto-match certification courses for this domain.");
      return 0;
    }

    setLoadingDomainCerts(prev => ({ ...prev, [index]: true }));
    let candidateCourses = [];
    const query = `${skillName} ${outcome.role}`.trim();

    try {
      const response = await axiosHttp.get("/v2/courses", {
        params: { q: query, size: 50, page: 0, "sort-by": "rating:desc" },
        headers: { 'Cache-Control': 'no-cache' }
      });
      const resData = response.data;
      const raw = Array.isArray(resData) 
        ? resData 
        : (resData?.results?.courses?.data || resData?.results?.courses || resData?.data || resData?.results || resData?.courses || resData?.items || []);
      candidateCourses = Array.isArray(raw) ? raw : [];
    } catch (e) {
      console.error(`Auto match courses fetch error for domain ${outcome.role}:`, e);
    } finally {
      setLoadingDomainCerts(prev => ({ ...prev, [index]: false }));
    }

    if (candidateCourses.length === 0 && Array.isArray(itCertCourses) && itCertCourses.length > 0) {
      candidateCourses = itCertCourses;
    }

    if (candidateCourses.length === 0) {
      message.error(`No IT certification courses found for "${query}".`);
      return 0;
    }

    const keywords = [
      ...skillName.toLowerCase().split(/\s+/),
      ...outcome.role.toLowerCase().split(/\s+/),
      ...(outcome.keySkills || []),
      ...(outcome.certifications || [])
    ].filter(k => k && k.length >= 2);

    const escapeRegex = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const scoredMatched = candidateCourses
      .map(course => {
        const title = (course.title || course.name || "").toLowerCase();
        const desc = (course.headline || course.description || "").toLowerCase();
        let score = 0;
        keywords.forEach(kw => {
          const regex = new RegExp(`\\b${escapeRegex(kw.toLowerCase())}\\b`, 'i');
          if (regex.test(title)) score += 5;
          else if (regex.test(desc)) score += 1;
        });
        if (title.includes("certifi") || title.includes("professional")) score += 2;
        return { course, score };
      })
      .filter(item => item.score > 2)
      .sort((a, b) => b.score - a.score);

    if (scoredMatched.length === 0) {
      message.warning(`No specifically matching courses found for "${outcome.role}".`);
      return 0;
    }

    const selectedList = scoredMatched.slice(0, 4).map(item => item.course._id || item.course.id || item.course.title);
    const currentList = outcome.certificationCourses || [];
    const merged = Array.from(new Set([...currentList, ...selectedList]));
    
    outcomes[index].certificationCourses = merged;
    pageForm.setFieldsValue({ careerOutcomes: [...outcomes] });
    message.success(`Auto-matched ${selectedList.length} relevant courses for "${outcome.role}"!`);
    return selectedList.length;
  };

  const handleAutoMatchAllDomains = async () => {
    const outcomes = pageForm.getFieldValue("careerOutcomes") || [];
    if (outcomes.length === 0) {
      return message.warning("No career outcomes available to match.");
    }
    let totalMatched = 0;
    for (let i = 0; i < outcomes.length; i++) {
      if (outcomes[i].role) {
        const matched = await handleAutoMatchDomainCertifications(i);
        totalMatched += matched;
      }
    }
    if (totalMatched > 0) {
      message.success(`Successfully auto-matched a total of ${totalMatched} courses across all domains!`);
    }
  };

  const [runningExa, setRunningExa] = useState({
    quickStats: false,
    overview: false,
    careers: false,
    whereUsed: false,
    guidance: false,
  });
  const [runningGlobalExa, setRunningGlobalExa] = useState(false);
  const [globalProgress, setGlobalProgress] = useState(null);

  const [configModal, setConfigModal] = useState({
    visible: false,
    category: "Skill Page Builder",
    defaultPrompt: "",
    contextInstructions: "",
    hidePromptsTab: false
  });

  const [exaConfigModal, setExaConfigModal] = useState({
    visible: false,
    section: "quickStats",
  });
  const [generatingSection, setGeneratingSection] = useState({
    seo: false,
    overview: false,
    roadmap: false,
    faq: false,
    ecosystem: false,
  });

  const skillAssignedTags = useMemo(() => {
    const skillTopicTags = Array.isArray(editingPage?.skill?.tags) ? editingPage.skill.tags : [];
    const pageTags = Array.isArray(editingPage?.tags) ? editingPage.tags : [];

    const tagMap = new Map();
    [...availableTags, ...skillTopicTags, ...pageTags].forEach(t => {
      if (typeof t === 'object' && t !== null && t._id) {
        const isTopicTag = skillTopicTags.some(st => String(typeof st === 'object' ? st._id : st) === String(t._id));
        const isPageTag = pageTags.some(pt => String(typeof pt === 'object' ? pt._id : pt) === String(t._id));
        if (isTopicTag || isPageTag) {
          tagMap.set(String(t._id), t);
        }
      }
    });

    if (tagMap.size === 0 && skillTopicTags.length === 0) {
      return availableTags;
    }

    return Array.from(tagMap.values());
  }, [editingPage, availableTags]);

  const openConfig = (section) => {
    let category = "Skill Page Builder";
    let defaultPrompt = "";
    let contextInstructions = "";

    if (section === "seo") {
      category = "Skill Page Builder SEO";
      defaultPrompt = "Analyze the skill context. Generate high-quality SEO metadata optimized for ranking. Provide a highly targeted primary keyword, an engaging title suffix, a concise meta description (under 160 chars), 5-7 relevant secondary keywords, an engaging Open Graph title (ogTitle), and an Open Graph description (ogDescription).";
      contextInstructions = "Return JSON with keys: primaryKeyword, titleSuffix, shortDescription, keywords (array of strings), ogTitle, ogDescription.";
    } else if (section === "overview") {
      category = "Skill Page Builder Overview";
      defaultPrompt = "Generate skill overview, description, relevance, and demand.";
      contextInstructions = "Return JSON with keys: description, whyRelevant, industryDemand, whereUsed.";
    } else if (section === "roadmap") {
      category = "Skill Page Builder Roadmap";
      defaultPrompt = "Generate learning roadmap stages (Beginner, Intermediate, Advanced).";
      contextInstructions = "Return JSON with key 'stages' containing beginner, intermediate, advanced details.";
    } else if (section === "faq") {
      category = "Skill Page Builder FAQ";
      defaultPrompt = "Generate frequently asked questions for the skill.";
      contextInstructions = "Return JSON with key 'faq' containing questions and answers.";
    } else if (section === "ecosystem") {
      category = "Skill Page Builder Ecosystem";
      defaultPrompt = "Generate related ecosystems, semantic context, and skill pairings.";
      contextInstructions = "Return JSON with keys: relatedEcosystems, semanticContext, careerProgressionNote, skillPairings.";
    } else if (section === "guidance" || section === "editorial") {
      category = "Skill Page Builder Guidance";
      defaultPrompt = "Generate an authoritative editorial perspective and expert mentor guidance for this skill. Include real-world trade-offs, learning philosophy, the #1 critical beginner pitfall, and actionable rectification.";
      contextInstructions = "Return JSON with keys: editorialSubtitle, editorialNote (well-formatted HTML starting with an <h3>Passage Title</h3> headline, followed by <p>, <strong>, <ul>/<li>), mentorAdvice, commonMistake, rectification.";
    }

    const hidePromptsTab = ["faq", "seo", "ecosystem", "guidance", "editorial"].includes(section);

    setConfigModal({
      visible: true,
      category,
      defaultPrompt,
      contextInstructions,
      hidePromptsTab
    });
  };


  const openExaConfig = (section) => {
    setExaConfigModal({
      visible: true,
      section
    });
  };

  const extractStructuredData = (data) => {
    if (!data) return {};

    const findKeys = (obj) => {
      if (!obj) return null;
      if (typeof obj === 'string') {
        try {
          const parsed = JSON.parse(obj);
          if (parsed && typeof parsed === 'object') {
            return findKeys(parsed);
          }
        } catch (e) { }
        return null;
      }
      if (typeof obj !== 'object') return null;

      if (obj.quickStats || obj.overview || obj.careers || obj.faqs || obj.faq || obj.primaryKeyword || obj.stages || obj.roadmap || obj.ecosystem || obj.semanticContext || obj.careerProgressionNote || obj.commonMistake || obj.common_mistake || obj.rectification || obj.pro_rectification || obj.mentorAdvice || obj.mentor_advice || obj.advice || obj.relatedEcosystems || obj.skillPairings || obj.whereUsed || obj.editorialNote || obj.editorial_note || obj.editorialSubtitle || obj.editorial_subtitle || obj.editorialTitle || obj.guidance) {
        return obj;
      }

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const val = obj[key];
          if (typeof val === 'string') {
            try {
              const parsed = JSON.parse(val);
              if (parsed && typeof parsed === 'object') {
                const res = findKeys(parsed);
                if (res) return res;
              }
            } catch (e) { }
          } else if (typeof val === 'object' && val !== null) {
            const res = findKeys(val);
            if (res) return res;
          }
        }
      }
      return null;
    };

    const common = data.output?.content || data.output?.structured || data.output || data.structured || data.data?.output?.content || data.data?.output || data.data;
    const commonRes = findKeys(common);
    if (commonRes) return commonRes;

    const deepRes = findKeys(data);
    if (deepRes) return deepRes;

    return data;
  };

  const handleExaResults = (section, data) => {
    const structuredData = extractStructuredData(data);

    const updates = {};

    if (structuredData.quickStats) {
      updates.demandSignal = structuredData.quickStats.demandSignal;
      updates.demandSource = structuredData.quickStats.demandSource || structuredData.quickStats.demandSignal_source || structuredData.demandSource || "";
      updates.quickStats = {
        ...pageForm.getFieldValue("quickStats"),
        paths: structuredData.quickStats.paths,
        salaryRange: structuredData.quickStats.salaryRange,
        level: structuredData.quickStats.level,
      };
    }

    if (structuredData.overview && section !== "whereUsed") {
      updates.overview = {
        ...pageForm.getFieldValue("overview"),
        whyRelevant: structuredData.overview.whyRelevant,
        industryDemand: structuredData.overview.industryDemand,
        whereUsed: mapWhereUsedToObjects(structuredData.overview.whereUsed),
      };
      if (structuredData.overview.description) {
        updates.overview.description = structuredData.overview.description;
      }
    }

    if (section === "whereUsed" || structuredData.whereUsed) {
      const rawWhereUsed = structuredData.whereUsed || (typeof data === 'object' ? (data.whereUsed || data) : null);
      const currentOverview = pageForm.getFieldValue("overview") || {};
      updates.overview = {
        ...currentOverview,
        whereUsed: mapWhereUsedToObjects(rawWhereUsed)
      };
    }

    if (structuredData.prerequisites) {
      updates.prerequisites = Array.isArray(structuredData.prerequisites) ? structuredData.prerequisites : String(structuredData.prerequisites).split(',').map(s => s.trim()).filter(Boolean);
    }

    if (structuredData.subtitle) {
      updates.subtitle = structuredData.subtitle;
    }

    if (structuredData.faqs) {
      updates.faq = structuredData.faqs;
    }

    if (section === "guidance" || section === "editorial" || structuredData.editorialSubtitle || structuredData.editorial_subtitle || structuredData.editorialNote || structuredData.editorial_note || structuredData.guidance || structuredData.commonMistake || structuredData.common_mistake || structuredData.mentorAdvice || structuredData.mentor_advice || structuredData.advice) {
      const g = structuredData.guidance || structuredData;
      const sub = g.editorialSubtitle || g.editorial_subtitle || g.subtitle;
      if (sub) updates.editorialSubtitle = sub;
      const note = g.editorialNote || g.editorial_note || g.note || g.editorial;
      if (note) updates.editorialNote = note;
      const mistake = g.commonMistake || g.common_mistake || g.pitfall || g.mistake;
      if (mistake) updates.commonMistake = mistake;
      const fix = g.rectification || g.pro_rectification || g.fix;
      if (fix) updates.rectification = fix;
      const adv = g.mentorAdvice || g.mentor_advice || g.advice || g.expertMentor?.advice;
      if (adv) {
        const currentMentor = pageForm.getFieldValue("expertMentor") || {};
        updates.expertMentor = {
          ...currentMentor,
          advice: adv
        };
      }
    }

    if (structuredData.curationText) {
      updates.curationText = structuredData.curationText;
    }

    if (structuredData.careers) {
      const allCerts = new Set(pageForm.getFieldValue("certifications") || []);
      
      updates.careerOutcomes = structuredData.careers.map(c => {
        const rawSkills = Array.isArray(c.keySkills) ? c.keySkills : (Array.isArray(c.topSkills) ? c.topSkills : (typeof (c.keySkills || c.topSkills) === 'string' ? String(c.keySkills || c.topSkills).split(',').map(s => s.trim()) : []));
        const rawCerts = Array.isArray(c.certifications) ? c.certifications : (Array.isArray(c.recommendedCertifications) ? c.recommendedCertifications : (typeof (c.certifications || c.recommendedCertifications) === 'string' ? String(c.certifications || c.recommendedCertifications).split(',').map(s => s.trim()) : []));
        const rawCompanies = Array.isArray(c.companies) ? c.companies : (Array.isArray(c.hiringCompanies) ? c.hiringCompanies : (typeof (c.companies || c.hiringCompanies) === 'string' ? String(c.companies || c.hiringCompanies).split(',').map(s => s.trim()) : []));
        
        const finalSkills = rawSkills.map(item => typeof item === 'object' && item !== null ? Object.values(item)[0] || "" : String(item)).filter(Boolean);
        const finalCerts = rawCerts.map(item => typeof item === 'object' && item !== null ? Object.values(item)[0] || "" : String(item)).filter(Boolean);
        const finalCompanies = rawCompanies.map(item => typeof item === 'object' && item !== null ? Object.values(item)[0] || "" : String(item)).filter(Boolean);
        
        finalCerts.forEach(cert => allCerts.add(cert));

        return {
          role: c.role || "",
          trend: c.trend || "",
          salary: c.salary || "",
          description: c.description || "",
          keySkills: finalSkills,
          certifications: finalCerts,
          companies: finalCompanies
        };
      });
      
      updates.certifications = Array.from(allCerts);
    }

    if (structuredData.metadata) {
      if (structuredData.metadata.salaryRange) updates.salaryRange = structuredData.metadata.salaryRange;
      if (structuredData.metadata.salaryRange_source) updates.salarySource = structuredData.metadata.salaryRange_source;

      if (structuredData.metadata.industryTrend) updates.industryTrend = structuredData.metadata.industryTrend;
      if (structuredData.metadata.industryTrend_source) updates.trendSource = structuredData.metadata.industryTrend_source;

      if (structuredData.metadata.learningTimeEstimate) updates.learningTimeEstimate = structuredData.metadata.learningTimeEstimate;
      if (structuredData.metadata.learningTimeEstimate_source) updates.timeSource = structuredData.metadata.learningTimeEstimate_source;
    }

    if (structuredData.audience && Array.isArray(structuredData.audience)) {
      updates.whoShouldLearn = structuredData.audience;
    }

    if (structuredData.us || structuredData.eu || structuredData.in || structuredData.regionalSalaries || structuredData.salaryInsights) {
      const salaryInsightsList = [];
      const source = structuredData.source || structuredData.regionalSalaries?.source || "";

      const usData = structuredData.us || structuredData.regionalSalaries?.us;
      if (usData && (usData.min || usData.median || usData.max)) {
        salaryInsightsList.push({
          region: "United States",
          currency: usData.currency || "USD",
          source: source || "Levels.fyi & Glassdoor (2026)",
          min: Number(usData.min) || 0,
          median: Number(usData.median) || 0,
          max: Number(usData.max) || 0,
        });
      }

      const euData = structuredData.eu || structuredData.regionalSalaries?.eu;
      if (euData && (euData.min || euData.median || euData.max)) {
        salaryInsightsList.push({
          region: "Europe",
          currency: euData.currency || "EUR",
          source: source || "Glassdoor (2026)",
          min: Number(euData.min) || 0,
          median: Number(euData.median) || 0,
          max: Number(euData.max) || 0,
        });
      }

      const inData = structuredData.in || structuredData.regionalSalaries?.in;
      if (inData && (inData.min || inData.median || inData.max)) {
        salaryInsightsList.push({
          region: "India",
          currency: inData.currency || "INR",
          source: source || "AmbitionBox & Glassdoor (2026)",
          min: Number(inData.min) || 0,
          median: Number(inData.median) || 0,
          max: Number(inData.max) || 0,
        });
      }

      if (Array.isArray(structuredData.salaryInsights) && structuredData.salaryInsights.length > 0) {
        updates.salaryInsights = structuredData.salaryInsights;
      } else if (salaryInsightsList.length > 0) {
        updates.salaryInsights = salaryInsightsList;
      }

      updates.regionalSalaries = structuredData.regionalSalaries || {
        us: structuredData.us,
        eu: structuredData.eu,
        in: structuredData.in,
        source: source || structuredData.source
      };
    }

    if (structuredData.stages || structuredData.roadmap) {
      updates.stages = structuredData.stages || structuredData.roadmap;
    }

    pageForm.setFieldsValue(updates);
  };

  const handleGlobalExaSearch = async () => {
    const skillName = pageForm.getFieldValue("name");
    if (!skillName) {
      message.warning("A skill name is required to run a web search.");
      return;
    }

    const steps = [
      { id: "definition", label: "Exa Search: Definition", fn: () => handleRunExaSearchDirect("definition") },
      { id: "overview", label: "AI Generator: Overview", fn: () => handleGenerateSection("overview") },
      { id: "quickStats", label: "Exa Search: Quick Stats", fn: () => handleRunExaSearchDirect("quickStats") },
      { id: "careers", label: "Exa Search: Careers", fn: () => handleRunExaSearchDirect("careers") },
      { id: "salaryInsights", label: "Exa Search: Salary Insights", fn: () => handleRunExaSearchDirect("salaryInsights") },
      { id: "roadmap", label: "Exa Search: Roadmap", fn: handleRoadmapExaSearch },
      { id: "ecosystem", label: "Exa Search: Ecosystem", fn: handleEcosystemExaSearch },
      { id: "guidance", label: "Enhanced AI Generation: Guidance", fn: () => handleGenerateEnhancedGuidance() },
      { id: "faq", label: "Enhanced AI Generation: FAQs", fn: handleGenerateEnhancedFaq },
      { id: "seo", label: "Enhanced AI Generation: SEO Meta", fn: handleGenerateEnhancedSeo }
    ];

    const totalSteps = steps.length;

    setRunningGlobalExa(true);
    message.loading({ content: `Starting AutoFill for ${totalSteps} sections...`, key: "globalExa" });
    setGlobalProgress({ current: 0, total: totalSteps, section: "" });

    const isSectionFilled = (id, vals) => {
      switch (id) {
        case "definition": return !!vals.overview?.description;
        case "overview": return !!vals.overview?.whyRelevant || !!vals.overview?.whereUsed?.length;
        case "quickStats": return !!vals.quickStats?.paths || !!vals.quickStats?.salaryRange || !!vals.demandSignal;
        case "careers": return vals.careers?.length > 0;
        case "salaryInsights": return vals.salaryInsights?.length > 0;
        case "roadmap": return vals.stages?.length > 0;
        case "ecosystem": return !!vals.semanticContext || !!vals.careerProgressionNote || vals.relatedEcosystems?.length > 0;
        case "guidance": return !!vals.editorialNote || !!vals.editorialTitle;
        case "faq": return vals.faqs?.length > 0;
        case "seo": return !!vals.seo?.shortDescription || !!vals.seo?.primaryKeyword;
        default: return false;
      }
    };

    try {
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];

        const vals = pageForm.getFieldsValue(true);
        if (isSectionFilled(step.id, vals)) {
          const shouldRegenerate = await new Promise((resolve) => {
            Modal.confirm({
              title: 'Section Already Populated',
              content: `The section "${step.label}" already contains data. Do you want to regenerate and overwrite it, or skip to the next section?`,
              okText: 'Regenerate',
              cancelText: 'Skip',
              centered: true,
              onOk: () => resolve(true),
              onCancel: () => resolve(false)
            });
          });

          if (!shouldRegenerate) {
            continue; // Skip this step
          }
        }

        setGlobalProgress({ current: i, total: totalSteps, section: step.label });
        await step.fn();
      }

      setGlobalProgress({ current: totalSteps, total: totalSteps, section: "Done!" });
      message.success({ content: "AutoFill completed successfully!", key: "globalExa" });

      Modal.confirm({
        title: 'AutoFill Completed',
        content: 'All sections up to Meta Tags have been processed. Would you like to save these changes now?',
        okText: 'Yes, Save Changes',
        cancelText: 'Review First',
        onOk: () => {
          handlePageSubmit();
        }
      });
    } catch (error) {
      console.error("AutoFill failed:", error);
      message.error({ content: `AutoFill failed: ${error.message}`, key: "globalExa" });
    } finally {
      setRunningGlobalExa(false);
      setTimeout(() => setGlobalProgress(null), 3000);
    }
  };

  const handleGenerateEnhancedFaq = async () => {
    const skillName = pageForm.getFieldValue("name");
    if (!skillName) {
      return message.warning("A skill name is required to run a web search.");
    }
    setGeneratingSection(prev => ({ ...prev, faq: true }));
    message.loading({ content: `Step 1: Searching live web for top questions about "${skillName}"...`, key: "faqGen" });
    try {
      const response = await axiosHttp.post("/v2/admin/exa/skill-structured", {
        skill: skillName,
        section: "faq",
        type: "auto",
        useCache: false
      });
      const data = extractStructuredData(response.data?.results || response.data?.data || response.data);
      if (!data.faqs || !Array.isArray(data.faqs) || data.faqs.length === 0) {
        throw new Error("No FAQs found in search results");
      }

      message.loading({ content: `Step 2: AI crafting comprehensive answers for ${data.faqs.length} questions...`, key: "faqGen" });

      const config = SECTION_AI_CONFIGS.faq;
      const exaContext = data.faqs.map((f, i) => `${i + 1}. Question: ${f.question}\nDraft Answer: ${f.answer}`).join('\n\n');
      const userPrompt = `Here are targeted questions and draft answers fetched from the live web for this skill:\n\n${exaContext}\n\nPlease refine, enhance, and comprehensively rewrite these FAQs. Provide well-organized and authoritative answers that augment the provided information and the overall skill context. Ensure you output exactly 7 to 10 high-quality FAQs.`;

      let modelOverride = "@cf/meta/llama-3.3-70b-instruct-fp8-fast";
      try {
        const configResponse = await axiosHttp.get(`/v2/admin/ai/config?category=${config.category}`);
        if (configResponse.data?.results?.model) {
          modelOverride = configResponse.data.results.model;
        }
      } catch (err) {}

      const genResponse = await axiosHttp.post("/v2/ai/generate", {
        category: config.category,
        pageId: pageId,
        systemPrompt: "You are an expert career advisor and technical mentor. Answer the provided questions comprehensively and in a well-organized manner.",
        userPrompt,
        contextInstructions: config.contextInstructions,
        contextFields: config.defaultContextFields,
        modelOverride,
        output: {
          format: "json",
          keys: config.outputKeys
        }
      });

      const results = genResponse.data?.results || {};
      let parsedResults = extractStructuredData(results.normalized || results);

      if (typeof parsedResults === 'string') {
        try { parsedResults = JSON.parse(parsedResults); } catch (e) { }
      }

      let generatedFaqs = parsedResults.faq || parsedResults.faqs;
      if (!generatedFaqs && Array.isArray(parsedResults)) {
        generatedFaqs = parsedResults;
        parsedResults = { faqs: generatedFaqs };
      }

      if (generatedFaqs && Array.isArray(generatedFaqs) && generatedFaqs.length > 0) {
        config.applyResults(parsedResults, pageForm);
        message.success({ content: "Successfully fetched targeted questions and AI-crafted comprehensive answers!", key: "faqGen" });
      } else {
        console.error("Failed to parse FAQ array from:", parsedResults);
        throw new Error("Invalid AI response format: Expected an array of FAQs.");
      }
    } catch (error) {
      console.error("Enhanced FAQ Generation failed:", error);
      const detail = error.response?.data?.message || error.message || "Unknown error";
      message.error({ content: `Failed to run Enhanced FAQ generation: ${detail}`, key: "faqGen" });
    } finally {
      setGeneratingSection(prev => ({ ...prev, faq: false }));
    }
  };

  const handleGenerateEnhancedSeo = async () => {
    const skillName = pageForm.getFieldValue("name");
    if (!skillName) {
      return message.warning("A skill name is required to run a web search.");
    }
    setGeneratingSection(prev => ({ ...prev, seo: true }));
    message.loading({ content: `Step 1: Searching live web for SEO context and competitors for "${skillName}"...`, key: "seoGen" });
    try {
      const response = await axiosHttp.post("/v2/admin/exa/skill-structured", {
        skill: skillName,
        section: "seo",
        type: "auto",
        useCache: false
      });
      const data = extractStructuredData(response.data?.results || response.data?.data || response.data);

      message.loading({ content: `Step 2: AI crafting high-quality SEO metadata...`, key: "seoGen" });

      const config = SECTION_AI_CONFIGS.seo;
      const exaContext = JSON.stringify(data.seo || data);
      const userPrompt = `Here is competitor and live web SEO context for this skill:\n\n${exaContext}\n\nPlease analyze this context and generate high-quality SEO metadata optimized for ranking. Provide a highly targeted primary keyword, an engaging title suffix, a concise meta description (under 160 chars), 5-7 relevant secondary keywords, an engaging Open Graph title (ogTitle), and an Open Graph description (ogDescription).`;

      let modelOverride = "@cf/meta/llama-3.3-70b-instruct-fp8-fast";
      try {
        const configResponse = await axiosHttp.get(`/v2/admin/ai/config?category=${config.category}`);
        if (configResponse.data?.results?.model) {
          modelOverride = configResponse.data.results.model;
        }
      } catch (err) {}

      const genResponse = await axiosHttp.post("/v2/ai/generate", {
        category: config.category,
        pageId: pageId,
        systemPrompt: "You are an expert SEO specialist. Generate high-ranking meta tags based on the provided context.",
        userPrompt,
        contextInstructions: config.contextInstructions,
        contextFields: config.defaultContextFields,
        modelOverride,
        output: {
          format: "json",
          keys: config.outputKeys
        }
      });

      const results = genResponse.data?.results || {};
      let parsedResults = extractStructuredData(results.normalized || results);

      if (typeof parsedResults === 'string') {
        try { parsedResults = JSON.parse(parsedResults); } catch (e) { }
      }

      if (parsedResults && typeof parsedResults === 'object' && parsedResults.primaryKeyword) {
        config.applyResults(parsedResults, pageForm);
        message.success({ content: "Successfully fetched web context and AI-crafted SEO metadata!", key: "seoGen" });
      } else {
        console.error("Failed to parse SEO metadata from:", parsedResults);
        throw new Error("Invalid AI response format: Expected SEO metadata object.");
      }
    } catch (error) {
      console.error("Enhanced SEO Generation failed:", error);
      const detail = error.response?.data?.message || error.message || "Unknown error";
      message.error({ content: `Failed to run Enhanced SEO generation: ${detail}`, key: "seoGen" });
    } finally {
      setGeneratingSection(prev => ({ ...prev, seo: false }));
    }
  };

  const handleGenerateEnhancedGuidance = async () => {
    const skillName = pageForm.getFieldValue("name");
    if (!skillName) {
      return message.warning("A skill name is required to run a web search.");
    }
    setGeneratingSection(prev => ({ ...prev, guidance: true }));
    message.loading({ content: `Step 1: Searching live web for expert guidance and industry perspectives on "${skillName}"...`, key: "guidanceGen" });
    try {
      const response = await axiosHttp.post("/v2/admin/exa/skill-structured", {
        skill: skillName,
        section: "guidance",
        type: "auto",
        useCache: false
      });
      const data = extractStructuredData(response.data?.results || response.data?.data || response.data);

      message.loading({ content: `Step 2: AI synthesizing editorial note, mentor advice, and pitfall fixes...`, key: "guidanceGen" });

      const config = SECTION_AI_CONFIGS.guidance;
      const exaContext = JSON.stringify(data.guidance || data);
      const userPrompt = `Here is expert industry guidance, production realities, and trade-off context fetched from the live web for this skill:\n\n${exaContext}\n\nPlease analyze this context and synthesize an authoritative, well-structured Editorial Note (starting with a compelling <h3>Passage Title</h3> headline, followed by 3-4 in-depth paragraphs formatted with clean HTML tags like <p>, <strong>, and <ul>/<li> covering real-world production realities, architectural trade-offs, and tutorial myths vs engineering facts), an insightful mentor advice quote, the #1 critical technical pitfall/anti-pattern, and an actionable pro rectification fix.`;

      let modelOverride = "@cf/meta/llama-3.3-70b-instruct-fp8-fast";
      try {
        const configResponse = await axiosHttp.get(`/v2/admin/ai/config?category=${config.category}`);
        if (configResponse.data?.results?.model) {
          modelOverride = configResponse.data.results.model;
        }
      } catch (err) {}

      const genResponse = await axiosHttp.post("/v2/ai/generate", {
        category: config.category,
        pageId: pageId,
        systemPrompt: "You are an expert technical curator and senior engineering mentor. Generate an authoritative editorial note and actionable guidance for learners.",
        userPrompt,
        contextInstructions: config.contextInstructions,
        contextFields: config.defaultContextFields,
        modelOverride,
        output: {
          format: "json",
          keys: config.outputKeys
        }
      });

      const results = genResponse.data?.results || {};
      let parsedResults = extractStructuredData(results.normalized || results.result || results);
      if (typeof parsedResults === 'string') {
        try { parsedResults = JSON.parse(parsedResults); } catch (e) {}
      }

      const gCandidate = parsedResults?.guidance || parsedResults || {};
      const dataCandidate = data?.guidance || data || {};

      const hasAIGuidance = !!(gCandidate.editorialNote || gCandidate.editorial_note || gCandidate.editorialSubtitle || gCandidate.editorial_subtitle || gCandidate.commonMistake || gCandidate.common_mistake || gCandidate.mentorAdvice || gCandidate.mentor_advice || gCandidate.advice);
      const hasExaGuidance = !!(dataCandidate.editorialNote || dataCandidate.editorial_note || dataCandidate.editorialSubtitle || dataCandidate.editorial_subtitle || dataCandidate.commonMistake || dataCandidate.common_mistake || dataCandidate.mentorAdvice || dataCandidate.mentor_advice || dataCandidate.advice);

      if (hasAIGuidance) {
        config.applyResults(gCandidate, pageForm);
        message.success({ content: "Successfully generated editorial note and mentor guidance!", key: "guidanceGen" });
      } else if (hasExaGuidance) {
        config.applyResults(dataCandidate, pageForm);
        message.success({ content: "Successfully generated guidance from web insights!", key: "guidanceGen" });
      } else {
        throw new Error("Unable to synthesize guidance. Please check API responses.");
      }
    } catch (error) {
      console.error("Enhanced Guidance Generation failed:", error);
      const detail = error.response?.data?.message || error.message || "Unknown error";
      message.error({ content: `Failed to run Enhanced Guidance generation: ${detail}`, key: "guidanceGen" });
    } finally {
      setGeneratingSection(prev => ({ ...prev, guidance: false }));
    }
  };

  const handleEcosystemExaSearch = async () => {
    const skillName = pageForm.getFieldValue("name");
    if (!skillName) {
      return message.warning("A skill name is required to run a web search.");
    }
    setRunningExa(prev => ({ ...prev, ecosystem: true }));
    message.loading({ content: `Searching live web for ecosystems related to "${skillName}"...`, key: "ecoExa" });
    try {
      const response = await axiosHttp.post("/v2/admin/exa/skill-structured", {
        skill: skillName,
        section: "ecosystem",
        type: "auto",
        useCache: false
      });
      const data = extractStructuredData(response.data?.results || response.data?.data || response.data);
      if (data.ecosystem && Array.isArray(data.ecosystem)) {
        const mappedPairings = data.ecosystem.map(item => ({
          relatedTopic: item.name,
          relationType: "highly_related",
          relevanceScore: 90,
          pairingContext: item.description
        }));
        const ecosystemNames = data.ecosystem.map(item => item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
        pageForm.setFieldsValue({
          skillPairings: mappedPairings,
          relatedEcosystems: ecosystemNames,
          ...(data.semanticContext && { semanticContext: data.semanticContext }),
          ...(data.careerProgressionNote && { careerProgressionNote: data.careerProgressionNote }),
          ...(data.commonMistake && { commonMistake: data.commonMistake }),
          ...(data.rectification && { rectification: data.rectification })
        });

        message.success({ content: "Ecosystem populated from live web successfully!", key: "ecoExa" });
      } else {
        throw new Error("No ecosystem found in search results");
      }
    } catch (error) {
      console.error("Ecosystem Exa Search failed:", error);
      const detail = error.response?.data?.message || error.message || "Unknown error";
      message.error({ content: `Failed to run Ecosystem web search: ${detail}`, key: "ecoExa" });
    } finally {
      setRunningExa(prev => ({ ...prev, ecosystem: false }));
    }
  };

  const handleRoadmapExaSearch = async () => {
    const skillName = pageForm.getFieldValue("name");
    if (!skillName) {
      return message.warning("A skill name is required to run a web search.");
    }
    setRunningExa(prev => ({ ...prev, roadmap: true }));
    message.loading({ content: `Searching live web for roadmap curriculum for "${skillName}"...`, key: "roadmapExa" });
    try {
      const response = await axiosHttp.post("/v2/admin/exa/skill-structured", {
        skill: skillName,
        section: "roadmap",
        type: "auto",
        useCache: false
      });
      const data = extractStructuredData(response.data?.results || response.data?.data || response.data);
      if (data.stages && Array.isArray(data.stages)) {
        pageForm.setFieldsValue({ stages: data.stages });
        message.success({ content: "Roadmap populated from live web successfully!", key: "roadmapExa" });
      } else {
        throw new Error("No roadmap stages found in search results");
      }
    } catch (error) {
      console.error("Roadmap Exa Search failed:", error);
      const detail = error.response?.data?.message || error.message || "Unknown error";
      message.error({ content: `Failed to run Roadmap web search: ${detail}`, key: "roadmapExa" });
    } finally {
      setRunningExa(prev => ({ ...prev, roadmap: false }));
    }
  };

  const fetchTopics = async () => {
    try {
      const response = await axiosHttp.get("/v3/admin/topics");
      const data = response.data?.data || response.data?.results || response.data || [];
      setTopics(data);
    } catch (error) {
      console.error("Failed to fetch topics for skill pairings");
    }
  };


  const handleFetchHierarchy = async () => {
    if (!editingPage?._id) {
      message.error({ content: 'Skill page context missing', key: 'eco-hierarchy' });
      return;
    }

    setRunningExa((prev) => ({ ...prev, fetchHierarchy: true }));
    message.loading({ content: 'Fetching existing skill hierarchy from DB...', key: 'eco-hierarchy' });

    try {
      const response = await axiosHttp.get(`/v3/admin/skill-pages/${editingPage._id}/hierarchy`);
      const dbSubskills = response.data?.results || response.data?.data || [];

      if (!Array.isArray(dbSubskills) || dbSubskills.length === 0) {
        message.info({ content: 'No existing DB subskill hierarchy found for this skill.', key: 'eco-hierarchy' });
        return;
      }

      const formattedDbSubskills = dbSubskills.map(sub => ({
        name: sub.name,
        slug: sub.slug || sub.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        chapters: Array.isArray(sub.chapters)
          ? sub.chapters.map(ch => typeof ch === 'object' ? (ch.name || ch.title || String(ch)) : String(ch)).filter(Boolean)
          : []
      }));

      // Merge with existing form subskills if any
      const existingFormSubskills = pageForm.getFieldValue('subskills') || [];
      const mergedMap = new Map();

      // First insert existing form subskills
      existingFormSubskills.forEach(group => {
        const key = group.slug || group.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        if (key) {
          mergedMap.set(key, {
            name: group.name,
            slug: group.slug,
            chapters: new Set(Array.isArray(group.chapters) ? group.chapters : [])
          });
        }
      });

      // Merge fetched DB subskills
      formattedDbSubskills.forEach(group => {
        const key = group.slug || group.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        if (mergedMap.has(key)) {
          const existing = mergedMap.get(key);
          group.chapters.forEach(ch => existing.chapters.add(ch));
        } else {
          mergedMap.set(key, {
            name: group.name,
            slug: group.slug,
            chapters: new Set(group.chapters)
          });
        }
      });

      // Convert back to array format
      const finalMerged = Array.from(mergedMap.values()).map(g => ({
        name: g.name,
        slug: g.slug,
        chapters: Array.from(g.chapters)
      }));

      pageForm.setFieldsValue({ subskills: finalMerged });
      message.success({ content: `Fetched & merged ${dbSubskills.length} DB subskill group(s) successfully!`, key: 'eco-hierarchy' });

    } catch (error) {
      console.error('Failed to fetch DB hierarchy', error);
      const detail = error.response?.data?.message || error.message || 'Unknown error';
      message.error({ content: `Failed to fetch DB hierarchy: ${detail}`, key: 'eco-hierarchy' });
    } finally {
      setRunningExa((prev) => ({ ...prev, fetchHierarchy: false }));
    }
  };

  const handleGenerateEcosystemTree = async () => {
    if (!editingPage?.skill) {
      message.error({ content: 'Skill context missing', key: 'eco-hierarchy' });
      return;
    }

    setRunningExa((prev) => ({ ...prev, ecosystemTree: true }));
    message.loading({ content: 'AI is mapping out the ecosystem & creating subskills...', key: 'eco-hierarchy' });

    try {
      const response = await axiosHttp.post('/v2/admin/exa/ecosystem-hierarchy', {
        skill: editingPage.name || pageForm.getFieldValue('name'),
        parentId: editingPage.skill
      });

      let structuredGroups = response.data?.results?.structuredGroups || response.data?.structuredGroups || response.data?.data?.structuredGroups || [];

      if (structuredGroups.length === 1 && structuredGroups[0].chapters?.length > 4) {
        const items = structuredGroups[0].chapters;
        const chunkSize = Math.ceil(items.length / 3);
        const titles = ["Web & Core Frameworks", "Data & Messaging", "Tooling & Ecosystem"];
        const slugs = ["web-core-frameworks", "data-messaging", "tooling-ecosystem"];
        structuredGroups = [];
        for (let idx = 0; idx < items.length; idx += chunkSize) {
          const chunk = items.slice(idx, idx + chunkSize);
          const groupIdx = Math.floor(idx / chunkSize);
          if (chunk.length > 0) {
            structuredGroups.push({
              name: titles[groupIdx] || `Group ${groupIdx + 1}`,
              slug: slugs[groupIdx] || `group-${groupIdx + 1}`,
              chapters: chunk
            });
          }
        }
      }

      if (structuredGroups.length > 0) {
        pageForm.setFieldsValue({ subskills: structuredGroups });
      }

      message.success({ content: 'Ecosystem Tree generated successfully!', key: 'eco-hierarchy' });

    } catch (error) {
      console.error('Failed to generate ecosystem tree', error);
      message.error({ content: 'Failed to generate ecosystem tree', key: 'eco-hierarchy' });
    } finally {
      setRunningExa((prev) => ({ ...prev, ecosystemTree: false }));
    }
  };

  const handleCommitEcosystemSubskills = async () => {
    if (!editingPage?.skill) {
      message.error({ content: 'Skill context missing', key: 'eco-commit' });
      return;
    }

    const structuredGroups = pageForm.getFieldValue('subskills');
    if (!structuredGroups || structuredGroups.length === 0) {
      message.error({ content: 'No subskills found in the form to commit.', key: 'eco-commit' });
      return;
    }

    setRunningExa((prev) => ({ ...prev, ecosystemCommit: true }));
    message.loading({ content: 'Committing subskills to the database...', key: 'eco-commit' });

    try {
      const response = await axiosHttp.post('/v2/admin/exa/ecosystem-save-subskills', {
        parentId: editingPage.skill,
        structuredGroups
      });

      message.success({ content: 'Subskills successfully committed to database!', key: 'eco-commit' });

    } catch (error) {
      console.error('Failed to commit ecosystem subskills', error);
      message.error({ content: 'Failed to commit subskills to database', key: 'eco-commit' });
    } finally {
      setRunningExa((prev) => ({ ...prev, ecosystemCommit: false }));
    }
  };

  const PAGE_STATUS_COLORS = {
    missing: "default",
    draft: "gold",
    published: "green",
    archived: "red",
  };

  const STAGE_LEVEL_LABELS = {
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
  };

  const emptyStringArray = (value) => (Array.isArray(value) ? value : []);

  const mapWhereUsedToObjects = (whereUsed) => {
    return emptyStringArray(whereUsed).map(item => {
      if (typeof item === 'string') {
        const parts = item.split("|").map(p => p.trim());
        return {
          industry: parts[0] || "",
          companies: parts[1] ? parts[1].split(",").map(c => c.trim()).filter(Boolean) : [],
          usage: parts[2] || ""
        };
      }
      return {
        industry: item?.industry || item?.useCase || "",
        companies: Array.isArray(item?.companies) 
          ? item.companies 
          : (typeof item?.companies === 'string' ? item.companies.split(',').map(c => c.trim()).filter(Boolean) : []),
        usage: item?.usage || item?.description || ""
      };
    });
  };

  const normalizePageFormValues = (values, editingPage = {}) => ({
    ...values,
    seo: {
      ...((values.seo === undefined ? editingPage.seo : values.seo) || {}),
      keywords: emptyStringArray((values.seo === undefined ? editingPage.seo : values.seo)?.keywords).filter(Boolean),
    },
    quickStats: (values.quickStats === undefined ? editingPage.quickStats : values.quickStats) || {},
    overview: {
      ...((values.overview === undefined ? editingPage.overview : values.overview) || {}),
      whatIsTitle: values.overview?.whatIsTitle !== undefined ? values.overview.whatIsTitle : (values.whatIsTitle || editingPage.overview?.whatIsTitle || editingPage.whatIsTitle || ""),
      whyLearnTitle: values.overview?.whyLearnTitle !== undefined ? values.overview.whyLearnTitle : (values.whyLearnTitle || editingPage.overview?.whyLearnTitle || editingPage.whyLearnTitle || ""),
      whereUsed: mapWhereUsedToObjects(
        (values.overview === undefined ? editingPage.overview : values.overview)?.whereUsed
      ).filter(item => item && (item.industry || item.usage || (Array.isArray(item.companies) && item.companies.length > 0))),
    },
    whatIsTitle: values.whatIsTitle !== undefined ? values.whatIsTitle : (values.overview?.whatIsTitle || editingPage.whatIsTitle || editingPage.overview?.whatIsTitle || ""),
    whyLearnTitle: values.whyLearnTitle !== undefined ? values.whyLearnTitle : (values.overview?.whyLearnTitle || editingPage.whyLearnTitle || editingPage.overview?.whyLearnTitle || ""),
    tags: emptyStringArray(values.tags === undefined ? editingPage.tags : values.tags)
      .map(t => typeof t === 'object' ? t._id : t)
      .filter(Boolean),
    whoShouldLearn: emptyStringArray(values.whoShouldLearn === undefined ? editingPage.whoShouldLearn : values.whoShouldLearn).filter(Boolean),
    certifications: emptyStringArray(values.certifications === undefined ? editingPage.certifications : values.certifications).filter(Boolean),
    certificationCourses: emptyStringArray(values.certificationCourses === undefined ? editingPage.certificationCourses : values.certificationCourses).filter(Boolean),
    certificationsData: (() => {
      const raw = values.certificationsData || editingPage.certificationsData || {};
      const industryCerts = emptyStringArray(raw.industryCertifications)
        .filter(c => c && c.name)
        .map(c => ({
          name: String(c.name || "").trim(),
          issuer: String(c.issuer || "").trim(),
          bestFor: String(c.bestFor || "").trim(),
          level: String(c.level || "Professional").trim(),
          examCode: String(c.examCode || "").trim(),
          guidance: String(c.guidance || "").trim(),
          url: String(c.url || "").trim(),
          prepCourses: emptyStringArray(c.prepCourses).filter(Boolean),
          prepCollection: c.prepCollection || null,
        }));
      return {
        industryCertifications: industryCerts,
      };
    })(),
    prerequisites: emptyStringArray(values.prerequisites === undefined ? editingPage.prerequisites : values.prerequisites).filter(Boolean),
    relatedEcosystems: emptyStringArray(values.relatedEcosystems === undefined ? editingPage.relatedEcosystems : values.relatedEcosystems).filter(Boolean),
    careerOutcomes: emptyStringArray(values.careerOutcomes === undefined ? editingPage.careerOutcomes : values.careerOutcomes).filter((item) => item?.role || item?.description).map(item => {
      const keySkills = emptyStringArray(item.keySkills || item.topSkills).filter(Boolean);
      const itemCertifications = emptyStringArray(item.certifications || item.recommendedCertifications).filter(Boolean);
      const companies = emptyStringArray(item.companies || item.hiringCompanies).filter(Boolean);
      const certificatePrograms = emptyStringArray(item.certificatePrograms).filter(Boolean);
      return {
        ...item,
        keySkills,
        topSkills: keySkills,
        certifications: itemCertifications,
        recommendedCertifications: itemCertifications,
        companies,
        hiringCompanies: companies,
        certificatePrograms,
      };
    }),
    faq: emptyStringArray(values.faq === undefined ? editingPage.faq : values.faq).filter((item) => item?.question || item?.answer),
    skillPairings: emptyStringArray(values.skillPairings === undefined ? editingPage.skillPairings : values.skillPairings).filter((item) => item?.slug || item?.name),
    salaryInsights: emptyStringArray(values.salaryInsights === undefined ? editingPage.salaryInsights : values.salaryInsights).filter(Boolean),
    regionalSalaries: (() => {
      const insights = emptyStringArray(values.salaryInsights === undefined ? editingPage.salaryInsights : values.salaryInsights).filter(Boolean);
      if (Array.isArray(insights) && insights.length > 0) {
        const res = { us: {}, eu: {}, in: {}, source: "" };
        insights.forEach(item => {
          const reg = (item.region || "").toLowerCase();
          const target = reg.includes("united states") || reg.includes("us") ? "us" : (reg.includes("europe") || reg.includes("eu") ? "eu" : (reg.includes("india") || reg.includes("in") ? "in" : null));
          if (target) {
            res[target] = {
              min: Number(item.min) || 0,
              median: Number(item.median) || 0,
              max: Number(item.max) || 0,
              currency: item.currency || (target === "us" ? "USD" : target === "eu" ? "EUR" : "INR")
            };
            if (item.source && !res.source) res.source = item.source;
          }
        });
        return res;
      }
      return values.regionalSalaries || editingPage.regionalSalaries || {};
    })(),
    stages: emptyStringArray(values.stages === undefined ? editingPage.stages : values.stages).map((stage, idx) => ({
      ...stage,
      id: stage?.id || `stage-${idx + 1}`,
      level: stage?.level || (idx === 0 ? "beginner" : idx === 1 ? "intermediate" : "advanced"),
      assignedCourses: emptyStringArray(stage?.assignedCourses).filter(Boolean),
      enableApiFallback: !!stage?.enableApiFallback,
      isHidden: !!stage?.isHidden,
    })),
    exploreMenuTitle: values.exploreMenuTitle !== undefined ? values.exploreMenuTitle : (editingPage.exploreMenuTitle || ""),
    heroTitle: values.heroTitle !== undefined ? values.heroTitle : (editingPage.heroTitle || ""),
    heroSubtitle: values.heroSubtitle !== undefined ? values.heroSubtitle : (editingPage.heroSubtitle || ""),
    overviewTitle: values.overviewTitle !== undefined ? values.overviewTitle : (editingPage.overviewTitle || ""),
    overviewSubtitle: values.overviewSubtitle !== undefined ? values.overviewSubtitle : (editingPage.overviewSubtitle || ""),
    coursesTitle: values.coursesTitle !== undefined ? values.coursesTitle : (editingPage.coursesTitle || ""),
    coursesSubtitle: values.coursesSubtitle !== undefined ? values.coursesSubtitle : (editingPage.coursesSubtitle || ""),
    roadmapTitle: values.roadmapTitle !== undefined ? values.roadmapTitle : (editingPage.roadmapTitle || ""),
    roadmapSubtitle: values.roadmapSubtitle !== undefined ? values.roadmapSubtitle : (editingPage.roadmapSubtitle || ""),
    careersTitle: values.careersTitle !== undefined ? values.careersTitle : (editingPage.careersTitle || ""),
    careersSubtitle: values.careersSubtitle !== undefined ? values.careersSubtitle : (editingPage.careersSubtitle || ""),
    ecosystemTitle: values.ecosystemTitle !== undefined ? values.ecosystemTitle : (editingPage.ecosystemTitle || ""),
    ecosystemSubtitle: values.ecosystemSubtitle !== undefined ? values.ecosystemSubtitle : (editingPage.ecosystemSubtitle || ""),
    faqTitle: values.faqTitle !== undefined ? values.faqTitle : (editingPage.faqTitle || ""),
    faqSubtitle: values.faqSubtitle !== undefined ? values.faqSubtitle : (editingPage.faqSubtitle || ""),
    resourcesTitle: values.resourcesTitle !== undefined ? values.resourcesTitle : (editingPage.resourcesTitle || ""),
    resourcesSubtitle: values.resourcesSubtitle !== undefined ? values.resourcesSubtitle : (editingPage.resourcesSubtitle || ""),
    editorialTitle: values.editorialTitle !== undefined ? values.editorialTitle : (editingPage.editorialTitle || ""),
    editorialSubtitle: values.editorialSubtitle !== undefined ? values.editorialSubtitle : (editingPage.editorialSubtitle || ""),
    certificationsTitle: values.certificationsTitle !== undefined ? values.certificationsTitle : (editingPage.certificationsTitle || ""),
    certificationsSubtitle: values.certificationsSubtitle !== undefined ? values.certificationsSubtitle : (editingPage.certificationsSubtitle || ""),
    editorialAuthor: values.editorialAuthor !== undefined ? values.editorialAuthor : (editingPage.editorialAuthor || values.pageAuthor || editingPage.pageAuthor || ""),
    pageAuthor: values.pageAuthor !== undefined ? values.pageAuthor : (editingPage.pageAuthor || values.editorialAuthor || editingPage.editorialAuthor || ""),
    editorialNote: values.editorialNote !== undefined ? values.editorialNote : (editingPage.editorialNote || ""),
    recommendedCourses: emptyStringArray(values.recommendedCourses === undefined ? editingPage.recommendedCourses : values.recommendedCourses).filter(Boolean),
    hideComparisons: !!values.hideComparisons,
    hideCareerOutcomes: !!values.hideCareerOutcomes,
    hideLearningPaths: !!values.hideLearningPaths || !!values.hideRoadmap,
    hideRoadmap: !!values.hideRoadmap || !!values.hideLearningPaths,
    hideCertifications: !!values.hideCertifications,
    hideFaq: !!values.hideFaq,
    hideEditorial: !!values.hideEditorial,
  });

  const getApiErrorMessage = (error, fallback) => (
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message ||
    fallback
  );

  const StringList = ({ name, addLabel, placeholder, columns }) => (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <div>
          <div style={columns ? { display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: "16px", marginBottom: fields.length > 0 ? "16px" : 0 } : {}}>
            {fields.map((field) => (
              <Space key={field.key} align="baseline" style={{ display: "flex", marginBottom: columns ? 0 : 8, width: "100%" }}>
                <Form.Item {...field} style={{ flex: 1, marginBottom: 0 }}>
                  <Input placeholder={placeholder} suffix={<CloseOutlined onClick={() => remove(field.name)} style={{ color: '#bfbfbf', cursor: 'pointer' }} title="Remove" />} />
                </Form.Item>
              </Space>
            ))}
          </div>
          <Button type="dashed" onClick={() => add()} block>{addLabel}</Button>
        </div>
      )}
    </Form.List>
  );

  const StatusPill = ({ status }) => (
    <Tag color={PAGE_STATUS_COLORS[status]} style={{ margin: 0, textTransform: "uppercase", fontWeight: 700, fontSize: 10, padding: "0 6px" }}>
      {status || "missing"}
    </Tag>
  );

  const COMPREHENSIVE_PROMPT = `You are an expert labor market analyst creating metadata for a public skill page on a learning marketplace.

Research the target skill using recent web sources.

Guidelines:
- Prefer official documentation, government labor statistics, and reputable industry publications.
- Use representative US salary data.
- Synthesize information across multiple sources instead of copying any single source.
- Avoid unsupported claims and marketing language.
- Keep the writing concise, factual, and suitable for learners evaluating whether to acquire this skill.
- Avoid duplicate information across fields.
- If evidence is limited or conflicting, make conservative estimates.`;

  const SECTION_AI_CONFIGS = {
    seo: {
      category: "Skill Page Builder SEO",
      defaultPrompt: "Analyze the skill context. Generate a high-quality, engaging meta description (under 160 characters), 5-7 relevant SEO keywords, and Open Graph tags.",
      contextInstructions: "Return JSON with keys: primaryKeyword, titleSuffix, shortDescription, keywords (array of strings), ogTitle, ogDescription.",
      outputKeys: ["primaryKeyword", "titleSuffix", "shortDescription", "keywords", "ogTitle", "ogDescription"],
      userPrompt: "Generate SEO metadata including primary keyword, title suffix, description, keywords, ogTitle, and ogDescription.",
      defaultContextFields: ["name", "subtitle", "overview_description"],
      applyResults: (val, form) => {
        const currentSeo = form.getFieldValue("seo") || {};
        form.setFieldsValue({
          seo: {
            ...currentSeo,
            primaryKeyword: val.primaryKeyword || currentSeo.primaryKeyword || "",
            titleSuffix: val.titleSuffix || currentSeo.titleSuffix || "",
            shortDescription: val.shortDescription || val.description || "",
            keywords: Array.isArray(val.keywords) ? val.keywords : String(val.keywords || "").split(",").map(k => k.trim()).filter(Boolean),
            ogTitle: val.ogTitle || currentSeo.ogTitle || "",
            ogDescription: val.ogDescription || currentSeo.ogDescription || ""
          }
        });
      }
    },
    overview: {
      category: "Skill Page Builder Overview",
      defaultPrompt: "Generate skill overview, description, relevance, and demand.",
      contextInstructions: "Return JSON with keys: description, whyRelevant, industryDemand, whereUsed (array of strings).",
      outputKeys: ["description", "whyRelevant", "industryDemand", "whereUsed"],
      userPrompt: "Generate overview, relevance, demand signal, and where this skill is used.",
      defaultContextFields: ["name", "subtitle"],
      applyResults: (val, form) => {
        const currentOverview = form.getFieldValue("overview") || {};
        form.setFieldsValue({
          overview: {
            ...currentOverview,
            description: val.description,
            whyRelevant: val.whyRelevant,
            industryDemand: val.industryDemand,
            whereUsed: mapWhereUsedToObjects(val.whereUsed)
          }
        });
      }
    },
    roadmap: {
      category: "Skill Page Builder Roadmap",
      defaultPrompt: "Generate learning roadmap stages (Beginner, Intermediate, Advanced).",
      contextInstructions: "Return JSON with key 'stages' containing beginner, intermediate, advanced details. Each stage should have level (beginner/intermediate/advanced), title, subtitle, description, roadmapStep, learningObjective, expectedOutcome, estimatedDuration, learningIntent.",
      outputKeys: ["stages"],
      userPrompt: "Generate a 3-stage learning roadmap (beginner, intermediate, advanced).",
      defaultContextFields: ["name", "subtitle", "overview_description"],
      applyResults: (val, form) => {
        if (Array.isArray(val.stages)) {
          form.setFieldsValue({ stages: val.stages });
        }
      }
    },
    faq: {
      category: "Skill Page Builder FAQ",
      defaultPrompt: "Generate frequently asked questions for the skill.",
      contextInstructions: "Return JSON with key 'faq' containing questions and answers.",
      outputKeys: ["faq"],
      userPrompt: "Generate exactly 7-10 frequently asked questions.",
      defaultContextFields: ["name", "subtitle", "overview_description", "overview_whyRelevant"],
      applyResults: (val, form) => {
        if (Array.isArray(val.faq)) {
          form.setFieldsValue({ faq: val.faq });
        }
      }
    },
    ecosystem: {
      category: "Skill Page Builder Ecosystem",
      defaultPrompt: "Generate related ecosystems, semantic context, and skill pairings.",
      contextInstructions: "Return JSON with keys: relatedEcosystems (array of strings), semanticContext, careerProgressionNote, skillPairings (array of objects with slug, name, relationshipContext).",
      outputKeys: ["relatedEcosystems", "semanticContext", "careerProgressionNote", "skillPairings"],
      userPrompt: "Generate related ecosystems, semantic context, career progression note, and related skill pairings.",
      defaultContextFields: ["name", "subtitle", "overview_description"],
      applyResults: (val, form) => {
        form.setFieldsValue({
          relatedEcosystems: Array.isArray(val.relatedEcosystems) ? val.relatedEcosystems : String(val.relatedEcosystems || "").split(",").map(k => k.trim()).filter(Boolean),
          semanticContext: val.semanticContext,
          careerProgressionNote: val.careerProgressionNote,
          skillPairings: Array.isArray(val.skillPairings) ? val.skillPairings : []
        });
      }
    },
    whereUsed: {
      category: "Skill Page Builder Where Used",
      defaultPrompt: "Generate real-world industry use cases with at least 5 major, standard, authoritative enterprise hiring companies per industry using this skill.",
      contextInstructions: "Return JSON with key 'whereUsed' containing array of objects with industry, companies (array of at least 5 strings), and usage (description string).",
      outputKeys: ["whereUsed"],
      userPrompt: "Generate 4-6 distinct real-world industry use cases, each featuring at least 5 major, standard, authoritative hiring enterprise companies for this skill.",
      defaultContextFields: ["name", "subtitle"],
      applyResults: (val, form) => {
        const currentOverview = form.getFieldValue("overview") || {};
        const whereUsedData = val.whereUsed || val;
        if (Array.isArray(whereUsedData)) {
          form.setFieldsValue({
            overview: {
              ...currentOverview,
              whereUsed: mapWhereUsedToObjects(whereUsedData)
            }
          });
        }
      }
    },
    guidance: {
      category: "Skill Page Builder Guidance",
      defaultPrompt: "Generate an authoritative editorial perspective and expert mentor guidance for this skill. Include strategic takeaways, practical trade-offs, the #1 common pitfall, and actionable rectification.",
      contextInstructions: "Return JSON with keys: editorialSubtitle, editorialNote (clean HTML starting with an <h3>Passage Title</h3>, followed by <p>, <strong>, <ul>/<li>), mentorAdvice, commonMistake, rectification.",
      outputKeys: ["editorialSubtitle", "editorialNote", "mentorAdvice", "commonMistake", "rectification"],
      userPrompt: "Generate an authoritative editorial note and mentor guidance for this skill.",
      defaultContextFields: ["name", "subtitle", "overview_description", "overview_whyRelevant"],
      applyResults: (val, form) => {
        const g = val?.guidance || val || {};
        const updates = {};
        const sub = g.editorialSubtitle || g.editorial_subtitle || g.subtitle;
        if (sub) updates.editorialSubtitle = sub;
        const note = g.editorialNote || g.editorial_note || g.note || g.editorial;
        if (note) updates.editorialNote = note;
        const mistake = g.commonMistake || g.common_mistake || g.pitfall || g.mistake;
        if (mistake) updates.commonMistake = mistake;
        const fix = g.rectification || g.pro_rectification || g.fix;
        if (fix) updates.rectification = fix;
        const adv = g.mentorAdvice || g.mentor_advice || g.advice || g.expertMentor?.advice;
        if (adv) {
          const currentMentor = form.getFieldValue("expertMentor") || {};
          updates.expertMentor = {
            ...currentMentor,
            advice: adv
          };
        }
        form.setFieldsValue(updates);
      }
    }
  };


  useEffect(() => {
    fetchPage();
    fetchTopics();
    fetchItCertCourses();
    fetchCourseCollections();
    fetchAvailableTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const response = await axiosHttp.get(`/v3/admin/skill-pages/${pageId}`);
      const page = response.data?.data;
      if (!page) throw new Error("Skill page not found");

      // Normalize salaryInsights for form
      let initialSalaryInsights = [];
      if (Array.isArray(page.salaryInsights) && page.salaryInsights.length > 0) {
        initialSalaryInsights = page.salaryInsights;
      } else if (page.regionalSalaries || (typeof page.salaryInsights === 'object' && page.salaryInsights !== null)) {
        const sData = page.regionalSalaries || page.salaryInsights;
        const source = sData.source || "";
        if (sData.us) {
          initialSalaryInsights.push({
            region: "United States",
            currency: sData.us.currency || "USD",
            source: source || "Levels.fyi & Glassdoor (2026)",
            min: Number(sData.us.min) || 0,
            median: Number(sData.us.median) || 0,
            max: Number(sData.us.max) || 0,
          });
        }
        if (sData.eu) {
          initialSalaryInsights.push({
            region: "Europe",
            currency: sData.eu.currency || "EUR",
            source: source || "Glassdoor (2026)",
            min: Number(sData.eu.min) || 0,
            median: Number(sData.eu.median) || 0,
            max: Number(sData.eu.max) || 0,
          });
        }
        if (sData.in) {
          initialSalaryInsights.push({
            region: "India",
            currency: sData.in.currency || "INR",
            source: source || "AmbitionBox (2026)",
            min: Number(sData.in.min) || 0,
            median: Number(sData.in.median) || 0,
            max: Number(sData.in.max) || 0,
          });
        }
      }

      setEditingPage(page);
      setIsDraftEditMode(false);
      pageForm.setFieldsValue({
        ...page,
        salaryInsights: initialSalaryInsights,
        certificationsData: {
          industryCertifications: (page.certificationsData?.industryCertifications || []).map((c) => ({
            ...c,
            prepCourses: (c.prepCourses || []).map((pc) => (typeof pc === "object" ? pc._id : pc)),
            prepCollection: typeof c.prepCollection === "object" ? c.prepCollection?._id : c.prepCollection,
          })),
        },
        careerOutcomes: (page.careerOutcomes || []).map((co) => ({
          ...co,
          certificatePrograms: (co.certificatePrograms || []).map((cp) => (typeof cp === "object" ? cp._id : cp)),
        })),
        tags: (page.tags || []).map(t => typeof t === 'object' ? t._id : t),
        uspTag: page.uspTag?._id || page.uspTag || page.skill?.uspTag?._id || page.skill?.uspTag || null,
        stages: page.stages || [],
        seo: {
          keywords: [],
          ...(page.seo || {}),
        },
        quickStats: page.quickStats || {},
        overview: {
          ...(page.overview || {}),
          whereUsed: mapWhereUsedToObjects(page.overview?.whereUsed),
        },
        overview_prompt: "Generate skill applications and curation text.",
        roadmap_prompt: "Generate a 3-stage roadmap (beginner, intermediate, advanced).",
        faq_prompt: "Generate a list of frequently asked questions.",
        ecosystem_prompt: "Generate related ecosystem components, semantic context, and progressions.",
      });
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to load skill page"));
      navigate("/pages/list"); // Redirect back on error
    } finally {
      setLoading(false);
    }
  };

  const handleOgImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("ogImage", file);
    try {
      const response = await axiosHttp.post(
        `/v3/admin/skill-pages/${pageId}/og-image`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const url = response.data?.data?.ogImage;
      if (url) {
        setEditingPage(prev => ({ ...prev, ogImage: url }));
        message.success("OG image uploaded successfully.");
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to upload OG image"));
    }
  };

  const handlePageSubmit = async () => {
    try {
      const values = await pageForm.validateFields();
      setSaving(true);
      const processedTags = await ensureTagsCreated(values.tags);
      const payload = normalizePageFormValues({ ...values, tags: processedTags, uspTag: values.uspTag || null }, editingPage);
      const response = await axiosHttp.put(`/v3/admin/skill-pages/${pageId}`, payload);
      const updated = response.data?.data;
      if (!updated) throw new Error("Failed to save skill page");
      setEditingPage(updated);
      pageForm.setFieldsValue({
        ...updated,
        tags: (updated.tags || []).map(t => typeof t === 'object' ? t._id : t),
        uspTag: updated.uspTag?._id || updated.uspTag || null,
        overview: {
          ...(updated.overview || {}),
          whereUsed: mapWhereUsedToObjects(updated.overview?.whereUsed),
        }
      });
      message.success("Skill page saved.");
    } catch (error) {
      if (error?.errorFields) return;
      message.error(getApiErrorMessage(error, "Failed to save skill page"));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      setSaving(true);
      const values = await pageForm.validateFields();
      const processedTags = await ensureTagsCreated(values.tags);
      const payload = normalizePageFormValues({ ...values, tags: processedTags }, editingPage);
      await axiosHttp.put(`/v3/admin/skill-pages/${pageId}`, payload);

      const statusRes = await axiosHttp.patch(`/v3/admin/skill-pages/${pageId}/status`, { status: "published" });
      const updated = statusRes.data?.data || { ...editingPage, status: "published" };

      setEditingPage(updated);
      setIsDraftEditMode(false);
      pageForm.setFieldsValue({
        ...updated,
        tags: (updated.tags || []).map(t => typeof t === 'object' ? t._id : t),
        overview: {
          ...(updated.overview || {}),
          whereUsed: mapWhereUsedToObjects(updated.overview?.whereUsed),
        }
      });
      message.success("Skill page published successfully.");
    } catch (error) {
      if (error?.errorFields) return;
      message.error(getApiErrorMessage(error, "Failed to publish skill page"));
    } finally {
      setSaving(false);
    }
  };

  const changePageStatus = async (status) => {
    try {
      const response = await axiosHttp.patch(`/v3/admin/skill-pages/${pageId}/status`, { status });
      if (!response.data?.data) throw new Error("Failed to update status");
      message.success(`Skill page moved to ${status}.`);
      setEditingPage({ ...editingPage, status });
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to update page status"));
    }
  };

  const handleGenerateSection = async (section) => {
    const config = SECTION_AI_CONFIGS[section];
    if (!config) return;

    setGeneratingSection(prev => ({ ...prev, [section]: true }));
    try {
      let systemPrompt = config.defaultPrompt;
      let modelOverride = "@cf/meta/llama-3.3-70b-instruct-fp8-fast";
      let contextFields = config.defaultContextFields;
      let contextInstructions = config.contextInstructions;

      try {
        const configResponse = await axiosHttp.get(`/v2/admin/ai/config?category=${config.category}`);
        const savedConfig = configResponse.data?.results || {};
        if (savedConfig.systemPrompt) systemPrompt = savedConfig.systemPrompt;
        if (savedConfig.model) modelOverride = savedConfig.model;
        if (savedConfig.contextFields?.length > 0) contextFields = savedConfig.contextFields;
        if (savedConfig.internalInstructions) contextInstructions = savedConfig.internalInstructions;
      } catch (e) {
        console.warn("Could not load custom AI config, using defaults", e);
      }

      const genResponse = await axiosHttp.post("/v2/ai/generate", {
        category: config.category,
        pageId: pageId,
        systemPrompt,
        userPrompt: config.userPrompt,
        contextInstructions,
        contextFields,
        modelOverride,
        output: {
          format: "json",
          keys: config.outputKeys
        }
      });

      const results = genResponse.data?.results || {};
      const normalized = results.normalized || results;

      if (normalized) {
        config.applyResults(normalized, pageForm);
        message.success(`Successfully generated content for ${section}!`);
      } else {
        throw new Error("Empty AI response received.");
      }

    } catch (error) {
      console.error(`AI Generation failed for ${section}:`, error);
      const detail = error.response?.data?.message || error.message || "Unknown error";
      message.error(`Failed to generate ${section}: ${detail}`);
    } finally {
      setGeneratingSection(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleRunExaSearchDirect = async (section) => {
    const skillName = pageForm.getFieldValue("name");
    if (!skillName) {
      message.warning("A skill name is required to run a web search.");
      return;
    }

    setRunningExa(prev => ({ ...prev, [section]: true }));
    try {
      let systemPrompt = "";
      let rewritePrompt = "Rewrite the following raw data into engaging, concise paragraphs for a skill landing page. Preserve all statistics and facts exactly. Remove citation markers. Keep it under 3 sentences per field.";
      try {
        const exaCategory = `Exa - ${section}`;
        const configResponse = await axiosHttp.get(`/v2/admin/ai/config?category=${exaCategory}`);
        const savedConfig = configResponse.data?.results || {};
        if (savedConfig.systemPrompt) {
          systemPrompt = savedConfig.systemPrompt;
        }
        if (savedConfig.rewritePrompt) {
          rewritePrompt = savedConfig.rewritePrompt;
        }
      } catch (e) {
        console.warn("Could not load custom Exa config, using default", e);
      }

      if (!systemPrompt && (section === "quickStats" || section === "overview")) {
        systemPrompt = COMPREHENSIVE_PROMPT;
      }

      message.loading({ content: `Running Exa Web Search for "${skillName}"...`, key: "exaSearch" });
      const response = await axiosHttp.post("/v2/admin/exa/skill-structured", {
        skill: skillName,
        section,
        ...(systemPrompt ? { systemPrompt } : {}),
        ...(rewritePrompt ? { rewritePrompt } : {}),
        type: "auto",
        useCache: false
      });

      const data = response.data?.results || response.data?.data || response.data;
      if (response.data?.status && data) {
        handleExaResults(section, data);
        message.success({ content: "Web search data successfully applied!", key: "exaSearch" });
      } else {
        throw new Error(response.data?.message || "Search returned empty results.");
      }

    } catch (error) {
      console.error("Exa direct search failed:", error);
      const detail = error.response?.data?.message || error.message || "Unknown error";
      message.error({ content: `Failed to run web search: ${detail}`, key: "exaSearch" });
    } finally {
      setRunningExa(prev => ({ ...prev, [section]: false }));
    }
  };


  if (loading && !editingPage) {
    return (
      <Page title="Loading Page Builder...">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Spin size="large" tip="Loading Workspace..." />
        </div>
      </Page>
    );
  }

  return (
    <Page title={editingPage ? `Builder: ${editingPage.name}` : "Page Builder"}>
      <div className="page-wrapper" style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: 60 }}>

        {/* Workspace Sticky Header */}
        <div style={{
          position: 'sticky',
          top: 60,
          zIndex: 100,
          background: '#fff',
          padding: '16px 24px',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24
        }}>
          <div style={{ flex: 1, minWidth: 0, paddingRight: 24 }}>
            <Breadcrumb style={{ marginBottom: 4 }}>
              <Breadcrumb.Item>
                <Link to="/pages/list" style={{ color: '#64748b' }}>
                  <FaArrowLeft style={{ marginRight: 6, fontSize: 12 }} />
                  Topic Pages
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Page Builder</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {editingPage?.name || "Untitled Page"}
              </h2>
              <StatusPill status={isDraftEditMode ? "draft" : (editingPage?.status || "draft")} />
              <div style={{ fontSize: 13, color: "#94a3b8", fontFamily: "monospace", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                /{editingPage?.slug || "draft-slug"}
              </div>

              <Tooltip title="Run full Auto-Fill across all Exa & AI sections sequentially">
                <Button
                  type="dashed"
                  size="small"
                  icon={<GlobalOutlined style={{ color: !isEditable ? undefined : "#1890ff" }} />}
                  loading={runningGlobalExa}
                  onClick={handleGlobalExaSearch}
                  style={!isEditable ? { marginLeft: 4 } : { borderColor: "#1890ff", color: "#1890ff", marginLeft: 4 }}
                  disabled={!isEditable}
                >
                  AutoFill
                </Button>
              </Tooltip>

              <Tooltip title="AI Prompts & Search Settings">
                <Button 
                  size="small"
                  icon={<SettingOutlined />} 
                  disabled={!isEditable}
                  onClick={() => setConfigModal({
                    visible: true,
                    category: "Skill Page Builder",
                    defaultPrompt: "You are an expert curriculum designer and career advisor. Generate high quality skill details.",
                    contextInstructions: "Generate structured page content based on skill context.",
                    hidePromptsTab: false
                  })}
                />
              </Tooltip>
            </div>
          </div>

          <Space size="middle">
            {/* Status Transition Group */}
            <Space.Compact style={{ borderRadius: 6, overflow: 'hidden' }}>
              {editingPage?.status !== "published" && (
                <Button 
                  type="primary" 
                  style={{ backgroundColor: '#10b981', borderColor: '#10b981' }} 
                  onClick={handlePublish} 
                  icon={<FaCheck />}
                  loading={saving}
                >
                  Publish
                </Button>
              )}

              {editingPage?.status === "published" && (
                <Button 
                  onClick={() => changePageStatus("draft")} 
                  icon={<FaUndo style={{ color: '#faad14' }} />}
                >
                  Move to Draft
                </Button>
              )}

              {editingPage?.status === "archived" ? (
                <Button onClick={() => changePageStatus("draft")} icon={<FaUndo />}>
                  Restore
                </Button>
              ) : (
                <Button onClick={() => changePageStatus("archived")} danger icon={<FaArchive />}>
                  Archive
                </Button>
              )}
            </Space.Compact>

            {/* Dedicated Edit Unlock / Lock Toggle for Published Pages */}
            {editingPage?.status === "published" && !isDraftEditMode && (
              <Tooltip title="Unlock editor to modify published page">
                <Button 
                  onClick={() => setIsDraftEditMode(true)} 
                  icon={<FaEdit style={{ color: '#6366f1' }} />}
                  style={{ borderColor: '#c7d2fe', color: '#6366f1', background: '#f5f3ff' }}
                >
                  Edit
                </Button>
              </Tooltip>
            )}

            {editingPage?.status === "published" && isDraftEditMode && (
              <Tooltip title="Discard unsaved edits and lock editor">
                <Button 
                  onClick={() => {
                    setIsDraftEditMode(false);
                    pageForm.setFieldsValue({
                      ...editingPage,
                      overview: {
                        ...(editingPage.overview || {}),
                        whereUsed: mapWhereUsedToObjects(editingPage.overview?.whereUsed),
                      }
                    });
                  }} 
                  icon={<FaTimes />}
                >
                  Cancel Edit
                </Button>
              </Tooltip>
            )}

            <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 4px' }} />

            {isEditable && (
              <Button type="primary" loading={saving} onClick={handlePageSubmit} style={{ minWidth: 120, background: '#6366f1', borderColor: '#6366f1' }}>
                Save Changes
              </Button>
            )}
          </Space>
        </div>

        {/* Workspace Body */}
        <div className="container-fluid" style={{ maxWidth: 1200, margin: '0 auto' }}>

          {globalProgress && (
            <Card style={{ marginBottom: 24, borderRadius: 12, borderColor: '#10b981', background: '#f0fdf4' }} bodyStyle={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <Typography.Text strong style={{ color: '#047857', fontSize: 16 }}>
                  <ThunderboltOutlined style={{ marginRight: 8 }} />
                  AutoFill in Progress...
                </Typography.Text>
                <Typography.Text type="secondary">
                  Step {globalProgress.current} of {globalProgress.total}
                </Typography.Text>
              </div>
              <Progress
                percent={Math.round((globalProgress.current / globalProgress.total) * 100)}
                status="active"
                strokeColor={{ from: '#10b981', to: '#34d399' }}
              />
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 13 }}>
                Currently running: <span style={{ fontWeight: 600, color: '#064e3b' }}>{globalProgress.section}</span>
              </Typography.Text>
            </Card>
          )}

          <Form layout="vertical" form={pageForm} disabled={!isEditable}>
            <Card style={{ borderRadius: 12, borderColor: "#e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }} bodyStyle={{ padding: '8px 24px 24px' }}>
              <Tabs defaultActiveKey="1" size="large" tabBarGutter={32}>

                {/* TAB 1: Overview */}
                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Overview</span>} key="1">
                  <div style={{ paddingTop: 16 }}>
                    <div className="row">
                      <div className="col-md-4">
                        <Form.Item label="Topic Name (Internal / Universal)" name="name" rules={[{ required: true, message: "Name is required" }]}>
                          <Input size="large" />
                        </Form.Item>
                      </div>
                      <div className="col-md-4">
                        <Form.Item label="Explore Menu Link Title" name="exploreMenuTitle" extra="Used in Explore Menu (defaults to Topic Name if empty)">
                          <Input size="large" placeholder="e.g. Master Java Programming" />
                        </Form.Item>
                      </div>
                      <div className="col-md-4">
                        <Form.Item label="Hero Section Title (H1)" name="heroTitle" extra="Main H1 heading on Hero Section (defaults to Topic Name if empty)">
                          <Input size="large" placeholder="e.g. Complete Java Developer Roadmap 2026" />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="row" style={{ marginTop: 8 }}>
                      <div className="col-md-4">
                        <Form.Item label="Subtitle (Hero)" name="subtitle" extra="Primary subtitle displayed under Hero Title (H1)">
                          <Input size="large" />
                        </Form.Item>
                      </div>
                      <div className="col-md-4">
                        <Form.Item label="Hero Subtitle Override" name="heroSubtitle" extra="Explicit hero subtitle override (defaults to Subtitle if empty)">
                          <Input size="large" placeholder="e.g. Master modern Java development with hands-on projects..." />
                        </Form.Item>
                      </div>
                      <div className="col-md-4">
                        <Form.Item label="Page Curator / Guide Controller" name="pageAuthor" extra="Top-level curator & author attribution (e.g. Sarah Chen or ChoiceDekho Editorial Team)">
                          <Input size="large" placeholder="e.g. Sarah Chen, Lead Cloud Architect" />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="row" style={{ marginTop: 8 }}>
                      <div className="col-md-6">
                        <Form.Item label="Theme Color" shouldUpdate={(prevValues, currentValues) => prevValues.color !== currentValues.color}>
                          {({ getFieldValue }) => (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <Form.Item name="color" noStyle>
                                <Input size="large" placeholder="#14b8a6" style={{ flex: 1 }} />
                              </Form.Item>
                              <ColorPicker format="hex" value={getFieldValue('color')} onChange={(color) => pageForm.setFieldsValue({ color: typeof color === 'string' ? color : color.toHexString() })} />
                            </div>
                          )}
                        </Form.Item>
                      </div>
                      <div className="col-md-6">
                        <Form.Item label="Hero Color" shouldUpdate={(prevValues, currentValues) => prevValues.heroColor !== currentValues.heroColor}>
                          {({ getFieldValue }) => (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <Form.Item name="heroColor" noStyle>
                                <Input size="large" placeholder="#14b8a6" style={{ flex: 1 }} />
                              </Form.Item>
                              <ColorPicker format="hex" value={getFieldValue('heroColor')} onChange={(color) => pageForm.setFieldsValue({ heroColor: typeof color === 'string' ? color : color.toHexString() })} />
                            </div>
                          )}
                        </Form.Item>
                      </div>
                    </div>

                    <div className="row" style={{ marginTop: 8 }}>
                      <div className="col-md-4">
                        <Form.Item label="Hero Background Style" name="heroBackgroundStyle">
                          <Select size="large">
                            <Select.Option value="gradient-full">Full Gradient (Default)</Select.Option>
                            <Select.Option value="gradient-subtle">Subtle Gradient</Select.Option>
                            <Select.Option value="solid">Solid Background</Select.Option>
                          </Select>
                        </Form.Item>
                      </div>
                      <div className="col-md-4">
                        <Form.Item label="At a Glance Theme" name="quickStatsTheme">
                          <Select size="large">
                            <Select.Option value="dark">Dark (Default)</Select.Option>
                            <Select.Option value="light">Light</Select.Option>
                          </Select>
                        </Form.Item>
                      </div>
                      <div className="col-md-4">
                        <Form.Item label="At a Glance Color" shouldUpdate={(prevValues, currentValues) => prevValues.quickStatsColor !== currentValues.quickStatsColor}>
                          {({ getFieldValue }) => (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <Form.Item name="quickStatsColor" noStyle>
                                <Input size="large" placeholder="Defaults to Theme Color" style={{ flex: 1 }} />
                              </Form.Item>
                              <ColorPicker format="hex" value={getFieldValue('quickStatsColor')} onChange={(color) => pageForm.setFieldsValue({ quickStatsColor: typeof color === 'string' ? color : color.toHexString() })} />
                            </div>
                          )}
                        </Form.Item>
                      </div>
                    </div>

                    <div className="row" style={{ marginTop: 8 }}>
                      <div className="col-md-4">
                        <Form.Item label="Hero Pattern" name="heroPatternOverlay">
                          <Select size="large">
                            <Select.Option value="radial-glow">Radial Glow (Default)</Select.Option>
                            <Select.Option value="grid">Grid Pattern</Select.Option>
                            <Select.Option value="dots">Dot Pattern</Select.Option>
                            <Select.Option value="none">None</Select.Option>
                          </Select>
                        </Form.Item>
                      </div>
                      <div className="col-md-4">
                        <Form.Item label="Hero Title Size" name="heroTitleSize">
                          <Select size="large">
                            <Select.Option value="standard">Standard (Default)</Select.Option>
                            <Select.Option value="large">Large</Select.Option>
                            <Select.Option value="extra-large">Extra Large</Select.Option>
                          </Select>
                        </Form.Item>
                      </div>
                      <div className="col-md-4">
                        <Form.Item label="Hero Tag Style" name="heroTagStyle">
                          <Select size="large">
                            <Select.Option value="glassmorphism">Glassmorphism (Default)</Select.Option>
                            <Select.Option value="solid">Solid Colors</Select.Option>
                            <Select.Option value="outline">Outlines</Select.Option>
                          </Select>
                        </Form.Item>
                      </div>
                    </div>

                    <Card
                      size="small"
                      style={{ marginTop: 16, marginBottom: 16, borderRadius: 10, borderColor: "#e2e8f0", background: "#f8fafc" }}
                      title={
                        <span style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>
                          📊 Hero "At a Glance" Card Content Overrides
                        </span>
                      }
                      extra={
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          Fine-tune summarized Hero card tiles (leave empty to use automatic values)
                        </span>
                      }
                    >
                      <div className="row">
                        <div className="col-md-6">
                          <Form.Item
                            label="Top Roles (Hero Card)"
                            name={["quickStats", "topRoles"]}
                            extra="Pipe '|' or comma separated list of curated roles (e.g. Demand Planning Analyst | SAP IBP Consultant | Solution Architect)"
                          >
                            <Input placeholder="e.g. Demand Planning Analyst | SAP IBP Consultant" />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item
                            label="Time to Learn (Hero Card)"
                            name={["quickStats", "learningTimeEstimate"]}
                            extra="Custom duration text or narrative (e.g. 'Varies by career path and prior SAP experience' or '3-6 months')"
                          >
                            <Input placeholder="e.g. Varies by career path and prior experience" />
                          </Form.Item>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-4">
                          <Form.Item
                            label="Difficulty / Target Audience"
                            name={["quickStats", "level"]}
                            extra="e.g. Beginner | Supply Chain Professionals"
                          >
                            <Input placeholder="e.g. Beginner | Supply Chain Pros" />
                          </Form.Item>
                        </div>
                        <div className="col-md-5">
                          <Form.Item
                            label="Avg. Salary Range (Summary)"
                            name={["quickStats", "salaryRange"]}
                            extra="e.g. US $80k-$180k+ | EU €60k-€110k+ | India ₹8L-₹30L+"
                          >
                            <Input placeholder="US: $80k-$180k+ | EU: €60k-€110k+ | IN: ₹8L-₹30L+" />
                          </Form.Item>
                        </div>
                        <div className="col-md-3">
                          <Form.Item
                            label="Salary Source"
                            name={["quickStats", "salarySource"]}
                            extra="e.g. Glassdoor, 2026"
                          >
                            <Input placeholder="Glassdoor, 2026" />
                          </Form.Item>
                        </div>
                      </div>
                    </Card>

                    <Card
                      size="small"
                      style={{ marginTop: 16, marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafafa' }}
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                            <TagOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                            Skill Categorization Tags
                          </span>
                          <Form.Item shouldUpdate={(prev, curr) => prev.tags !== curr.tags} noStyle>
                            {() => {
                              const currentTags = pageForm.getFieldValue("tags") || [];
                              return (
                                <Tag color={currentTags.length > 0 ? "blue" : "default"}>
                                  {currentTags.length} {currentTags.length === 1 ? "Tag" : "Tags"} Selected
                                </Tag>
                              );
                            }}
                          </Form.Item>
                        </div>
                      }
                    >
                      <Form.Item
                        name="tags"
                        extra="Select centralized skill tags or type to create new ones (used for search discovery & hero section badges)."
                        style={{ marginBottom: 8 }}
                      >
                        <Select
                          disabled={!isEditable}
                          mode="tags"
                          size="large"
                          placeholder="Select existing tags or type to create new skill tags..."
                          showSearch
                          optionFilterProp="label"
                          tokenSeparators={[',']}
                          tagRender={(props) => {
                            const { value, closable, onClose, disabled } = props;
                            const tagObj = skillAssignedTags.find(t => String(t._id) === String(value)) || availableTags.find(t => String(t._id) === String(value)) || value;
                            const idx = skillAssignedTags.indexOf(tagObj);
                            const parsed = parseTagData(tagObj, idx >= 0 ? idx : 0);
                            const isInputDisabled = disabled || !isEditable;

                            return (
                              <Tag
                                closable={isInputDisabled ? false : closable}
                                onClose={onClose}
                                style={{
                                  background: isInputDisabled ? '#e2e8f0' : `${parsed.color}15`,
                                  borderColor: isInputDisabled ? '#cbd5e1' : `${parsed.color}40`,
                                  color: isInputDisabled ? '#64748b' : parsed.color,
                                  fontWeight: 600,
                                  fontSize: 12,
                                  padding: '2px 8px',
                                  borderRadius: 12,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  marginRight: 4
                                }}
                              >
                                {renderTagIcon(tagObj, idx)}
                                <span>{parsed.name}</span>
                              </Tag>
                            );
                          }}
                          options={skillAssignedTags.map((t, idx) => {
                            const parsed = parseTagData(t, idx);
                            return {
                              label: (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: parsed.color, fontWeight: 500 }}>
                                  {renderTagIcon(t, idx)}
                                  <span>{parsed.name}</span>
                                </span>
                              ),
                              value: t._id,
                              searchValue: parsed.name
                            };
                          })}
                        />
                      </Form.Item>

                      <Form.Item shouldUpdate={(prev, curr) => prev.tags !== curr.tags} noStyle>
                        {() => {
                          const currentTagIds = pageForm.getFieldValue("tags") || [];
                          const activeTagObjs = currentTagIds.map(id => {
                            return skillAssignedTags.find(t => String(t._id) === String(id)) || availableTags.find(t => String(t._id) === String(id)) || { _id: id, name: id };
                          });

                          return (
                            <Form.Item
                              label={
                                <span style={{ fontWeight: 600, fontSize: 13, color: '#334155', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: '#eab308' }}>★</span> Primary USP Tag (Appears in Explore Menu entry)
                                </span>
                              }
                              name="uspTag"
                              extra="Select one USP tag from the chosen skill tags. Its icon will be displayed alongside this topic link in the Explore Menu."
                              style={{ marginTop: 12, marginBottom: 0 }}
                            >
                              <Select
                                disabled={!isEditable || activeTagObjs.length === 0}
                                placeholder={activeTagObjs.length === 0 ? "Select skill tags above first..." : "Select one USP tag for the Explore Menu..."}
                                allowClear
                                style={{ width: '100%' }}
                                options={activeTagObjs.map((t, idx) => {
                                  const parsed = parseTagData(t, idx);
                                  return {
                                    label: (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: parsed.color, fontWeight: 600 }}>
                                        {renderTagIcon(t, idx)}
                                        <span>{parsed.name}</span>
                                        <Tag color="gold" style={{ marginLeft: 6, fontSize: 10, borderRadius: 10, lineHeight: '16px', padding: '0 6px' }}>★ USP</Tag>
                                      </span>
                                    ),
                                    value: t._id || t,
                                  };
                                })}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Card>

                    <Card
                      size="small"
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Market Data, Trends & Narrative</span>}
                      style={{ marginTop: 24, marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                      extra={
                        <Space>
                          <Tooltip title="Exa Search (Auto-fill stats & polish narrative)">
                            <Button
                              icon={<GlobalOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => handleRunExaSearchDirect("quickStats")}
                              loading={runningExa.quickStats}
                            />
                          </Tooltip>
                          <Tooltip title="Exa Search Settings">
                            <Button
                              icon={<SettingOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => openExaConfig("quickStats")}
                            />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <div className="row">
                        <div className="col-md-8"><Form.Item label="Demand Signal (2-3 Sentences)" name="demandSignal"><Input.TextArea rows={3} placeholder="Comprehensive 2-3 sentence hiring trend or demand signal narrative..." /></Form.Item></div>
                        <div className="col-md-4"><Form.Item label="Demand Source" name="demandSource"><Input placeholder="e.g. LinkedIn Workforce Report (2026)" /></Form.Item></div>
                      </div>
                      <div className="row" style={{ marginTop: 8 }}>
                        <div className="col-md-8"><Form.Item label="Salary Range Summary" name={["quickStats", "salaryRange"]}><Input placeholder="US: $100k-$150k | EU: €70k-€90k | IN: ₹10L-₹20L (Glassdoor, 2026)" /></Form.Item></div>
                        <div className="col-md-4"><Form.Item label="Target Level" name={["quickStats", "level"]}><Input placeholder="e.g. Beginner to Advanced" /></Form.Item></div>
                      </div>
                      <div className="row" style={{ marginTop: 8 }}>
                        <div className="col-md-6">
                          <Form.Item label="Overview Section Title" name="overviewTitle" extra="Heading displayed above Overview section (defaults to 'Overview & Industry Relevance' if empty).">
                            <Input placeholder="e.g. Java Overview & Industry Relevance" />
                          </Form.Item>
                          <Form.Item label="Overview Section Subtitle" name="overviewSubtitle" extra="Subtitle displayed under Overview section title">
                            <Input placeholder="e.g. Core architecture, real-world industry usage, and essential background..." />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="Courses Section Title" name="coursesTitle" extra="Heading displayed above Courses section (defaults to 'Top Rated Courses & Certifications' if empty).">
                            <Input placeholder="e.g. Top Rated Java Courses & Certifications" />
                          </Form.Item>
                          <Form.Item label="Courses Section Subtitle" name="coursesSubtitle" extra="Subtitle displayed under Courses section title">
                            <Input placeholder="e.g. Compare top-rated courses and certifications side-by-side..." />
                          </Form.Item>
                        </div>
                      </div>

                      <Divider style={{ margin: "16px 0" }} />

                      <div style={{ marginBottom: 16 }}>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, color: "#1e293b" }}>💡 Definition Card Heading</span>}
                          name={["overview", "whatIsTitle"]}
                          extra="Custom heading for the primary definition card (defaults to 'What is {Topic}?' if empty)"
                        >
                          <Input size="large" placeholder="e.g. What is Java? Architecture, JVM & Core Mechanics" />
                        </Form.Item>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, color: "#1e293b" }}>💡 Primary Definition & Overview (Rich Text)</span>}
                          name={["overview", "description"]}
                          extra="Comprehensive explanation of what the topic is, how it works, and core concepts."
                        >
                          <TipTapEditor editable={isEditable} placeholder="Write the core definition, architecture, key mechanisms, and technical foundation..." height="240px" />
                        </Form.Item>
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, color: "#1e293b" }}>🚀 Value Proposition Card Heading</span>}
                          name={["overview", "whyLearnTitle"]}
                          extra="Custom heading for the value proposition card (defaults to 'Why Learn {Topic}?' if empty)"
                        >
                          <Input size="large" placeholder="e.g. Why Learn Java? Enterprise Scale & High Industry Demand" />
                        </Form.Item>
                        <Form.Item
                          label={<span style={{ fontWeight: 600, color: "#1e293b" }}>🚀 Value Proposition & Industry Relevance (Rich Text)</span>}
                          name={["overview", "whyRelevant"]}
                          extra="Explain why learners should master this skill, industry relevance, and career value."
                        >
                          <TipTapEditor editable={isEditable} placeholder="Write the career relevance, demand factors, real-world utility, and key value propositions..." height="220px" />
                        </Form.Item>
                      </div>

                      <Form.Item label="Curation Text" name="curationText"><Input.TextArea rows={2} placeholder="e.g. Hand-picked courses focusing on..." /></Form.Item>
                      <Form.Item label="Industry Demand" name={["overview", "industryDemand"]}><Input.TextArea rows={2} /></Form.Item>
                      <Form.Item label="Prerequisites (Tags)" name="prerequisites" style={{ marginTop: 12 }}>
                        <Select mode="tags" style={{ width: '100%' }} placeholder="e.g. Basic Linux, Networking, PowerShell" tokenSeparators={[',']} />
                      </Form.Item>
                      <Card
                        size="small"
                        style={{ marginTop: 20, marginBottom: 0, borderRadius: 10, borderColor: '#cbd5e1', background: '#f8fafc' }}
                        title={
                          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                            <BankOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                            Where Used (Industry Cases & Companies)
                          </span>
                        }
                        extra={
                          <Space size="small">
                            <Tooltip title="Exa Search (Auto-fill Industry Use Cases & Companies only)">
                              <Button
                                size="small"
                                icon={<GlobalOutlined style={{ color: "#1890ff" }} />}
                                onClick={() => handleRunExaSearchDirect("whereUsed")}
                                loading={runningExa.whereUsed}
                              >
                                Exa Search
                              </Button>
                            </Tooltip>
                            <Tooltip title="Configure Where Used Exa Search Settings">
                              <Button
                                size="small"
                                icon={<SettingOutlined style={{ color: "#1890ff" }} />}
                                onClick={() => openExaConfig("whereUsed")}
                              />
                            </Tooltip>
                          </Space>
                        }
                      >
                        <Form.List name={["overview", "whereUsed"]}>
                          {(fields, { add, remove }) => (
                            <div>
                              {fields.map((field) => (
                                <Card 
                                  key={field.key} 
                                  size="small" 
                                  style={{ marginBottom: 16, borderRadius: 8, background: '#ffffff', borderColor: '#e2e8f0' }}
                                  extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}
                                >
                                  <div className="row">
                                    <div className="col-md-4">
                                      <Form.Item label="Industry / Domain" name={[field.name, "industry"]}>
                                        <Input placeholder="e.g. Finance" />
                                      </Form.Item>
                                    </div>
                                    <div className="col-md-8">
                                      <Form.Item label="Companies Using It" name={[field.name, "companies"]}>
                                        <Select 
                                          mode="tags" 
                                          tokenSeparators={[',']} 
                                          placeholder="Type company names and press Enter" 
                                          style={{ width: '100%' }} 
                                        />
                                      </Form.Item>
                                    </div>
                                  </div>
                                  <Form.Item label="How it is Used / Description" name={[field.name, "usage"]} style={{ marginBottom: 0 }}>
                                    <Input.TextArea rows={2} placeholder="Explain how this skill is applied in this industry..." />
                                  </Form.Item>
                                </Card>
                              ))}
                              <Button type="dashed" onClick={() => add()} block>Add Industry Use Case</Button>
                            </div>
                          )}
                        </Form.List>
                      </Card>
                    </Card>
                  </div>
                </Tabs.TabPane>

                {/* TAB 2: Ecosystem & Subskills */}
                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Ecosystem</span>} key="2" forceRender>
                  <div style={{ paddingTop: 16 }}>
                    <Card
                      size="small"
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>🌲 Subskills Hierarchy & Ecosystem Mapping</span>}
                      style={{ marginBottom: 20, borderRadius: 10, borderColor: "#e2e8f0", background: "#ffffff" }}
                      extra={
                        <Space>
                          <Button type="primary" onClick={handleGenerateEcosystemTree} loading={runningExa.ecosystemTree} icon={<GlobalOutlined />}>
                            Generate Ecosystem Tree (AI)
                          </Button>
                          <Button type="dashed" danger onClick={handleCommitEcosystemSubskills} loading={runningExa.ecosystemCommit}>
                            Commit Subskills to Database
                          </Button>
                          <Button type="default" onClick={handleFetchHierarchy} loading={runningExa.fetchHierarchy}>
                            Fetch DB Hierarchy
                          </Button>
                        </Space>
                      }
                    >
                      <Form.List name="subskills">
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card
                                key={field.key}
                                size="small"
                                style={{ marginBottom: 16, borderRadius: 8, background: '#fafafa', borderColor: '#e2e8f0' }}
                                extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}
                              >
                                <div className="row">
                                  <div className="col-md-6">
                                    <Form.Item label="Subskill Name" name={[field.name, "name"]}><Input /></Form.Item>
                                  </div>
                                  <div className="col-md-6">
                                    <Form.Item label="Subskill Slug" name={[field.name, "slug"]}><Input /></Form.Item>
                                  </div>
                                </div>
                                <Form.Item label="Chapters (Topics)" name={[field.name, "chapters"]} style={{ marginBottom: 0 }}>
                                  <Select mode="tags" style={{ width: '100%' }} placeholder="Type chapter name and press enter" />
                                </Form.Item>
                              </Card>
                            ))}
                            <Button type="dashed" onClick={() => add()} block>Add Subskill</Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>

                    <Card
                      size="small"
                      title={
                        <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
                          🌐 Ecosystem & Companion Context
                        </span>
                      }
                      style={{ marginBottom: 20, borderRadius: 10, borderColor: "#e2e8f0" }}
                      extra={
                        <Space>
                          <Tooltip title="Exa Search Settings">
                            <Button
                              icon={<SettingOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => openExaConfig("ecosystem")}
                            />
                          </Tooltip>
                          <Tooltip title="Cloudflare AI (Creative)">
                            <Button
                              icon={<SettingOutlined />}
                              onClick={() => openConfig("ecosystem")}
                            />
                          </Tooltip>
                          <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            loading={runningExa.ecosystem || generatingSection.ecosystem}
                            onClick={handleEcosystemExaSearch}
                            style={{
                              background: '#8b5cf6',
                              borderColor: '#8b5cf6',
                              borderRadius: 4
                            }}
                          >
                            Generate Ecosystem Context
                          </Button>
                        </Space>
                      }
                    >
                      <Form.Item
                        label="Related Ecosystems & Companion Tool Tags"
                        name="relatedEcosystems"
                        extra="Comma-separated or tag list of companion frameworks, runtimes, and subskill tools."
                      >
                        <Select mode="tags" style={{ width: '100%' }} placeholder="e.g. spring-boot, hibernate, docker, maven, kafka" tokenSeparators={[',']} />
                      </Form.Item>

                      <div className="row">
                        <div className="col-md-6">
                          <Form.Item
                            label="Semantic Context & Domain Narrative"
                            name="semanticContext"
                            extra="High-level narrative explaining how this skill fits into the broader engineering landscape."
                          >
                            <Input.TextArea rows={3} placeholder="e.g. Core language underpinning enterprise backend systems and microservice architectures..." />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item
                            label="Career Progression & Branching Note"
                            name="careerProgressionNote"
                            extra="Strategic guidance on how mastering this skill branches into specialized engineering tracks."
                          >
                            <Input.TextArea rows={3} placeholder="e.g. Transitioning from core developer to Distributed Systems Architect or Cloud Specialist..." />
                          </Form.Item>
                        </div>
                      </div>
                    </Card>

                    <Card
                      size="small"
                      title={
                        <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
                          🔗 Related Skills & Pairings
                        </span>
                      }
                      style={{ marginBottom: 20, borderRadius: 10, borderColor: "#e2e8f0" }}
                      extra={
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          Connect complementary skills and career progression paths
                        </span>
                      }
                    >
                      <Form.List name="skillPairings">
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card key={field.key} size="small" style={{ marginBottom: 12, borderRadius: 8, background: '#fafafa', borderColor: '#e2e8f0' }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                                <div className="row">
                                  <div className="col-md-6">
                                    <Form.Item label="Topic (Search & Select)" name={[field.name, "slug"]}>
                                      <Select
                                        showSearch
                                        placeholder="Search topic..."
                                        optionFilterProp="label"
                                        options={topics.map(t => ({
                                          label: `${t.name} ${t.page?.slug ? `(/${t.page.slug})` : ''}`,
                                          value: t.page?.slug || t.slug || t.code,
                                          topicName: t.name
                                        }))}
                                        onChange={(val, option) => {
                                          const pairings = pageForm.getFieldValue('skillPairings') || [];
                                          pairings[field.name] = {
                                            ...pairings[field.name],
                                            slug: option.value,
                                            name: option.topicName
                                          };
                                          pageForm.setFieldsValue({ skillPairings: pairings });
                                        }}
                                      />
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-6"><Form.Item label="Display Name" name={[field.name, "name"]}><Input placeholder="e.g. Docker & Kubernetes" /></Form.Item></div>
                                </div>
                                <Form.Item label="Relationship Context" name={[field.name, "relationshipContext"]} style={{ marginBottom: 0 }} extra="Explain how this related skill complements or branches from the current topic."><Input.TextArea rows={2} placeholder="e.g. Essential complementary skill for modern microservices and containerization..." /></Form.Item>
                              </Card>
                            ))}
                            <Button type="dashed" size="large" block onClick={() => add()}>Add Skill Pairing</Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>

                    <Card
                      size="small"
                      title={
                        <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
                          ⚖️ Skill Comparisons
                        </span>
                      }
                      style={{ borderRadius: 10, borderColor: "#e2e8f0" }}
                      extra={
                        <Space>
                          <Form.Item name="hideComparisons" valuePropName="checked" noStyle>
                            <Switch
                              checkedChildren="Comparisons Hidden"
                              unCheckedChildren="Comparisons Visible"
                              style={{ background: pageForm.getFieldValue("hideComparisons") ? '#ef4444' : '#22c55e' }}
                            />
                          </Form.Item>
                          <Button icon={<SettingOutlined />} onClick={() => openConfig("comparisons")} />
                          <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            loading={generatingSection.comparisons}
                            onClick={() => handleGenerateSection("comparisons")}
                            style={{
                              background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
                              border: 'none',
                              borderRadius: 4
                            }}
                          >
                            Generate Comparisons
                          </Button>
                        </Space>
                      }
                    >
                      <Form.List name="comparisons">
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card key={field.key} size="small" style={{ marginBottom: 12, borderRadius: 8, background: '#fafafa', borderColor: '#e2e8f0' }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                                <div className="row">
                                  <div className="col-md-6">
                                    <Form.Item label="Vs. Skill (Search & Select)" name={[field.name, "slug"]}>
                                      <Select
                                        showSearch
                                        placeholder="Search skill to compare..."
                                        optionFilterProp="label"
                                        options={topics.map(t => ({
                                          label: `${t.name} ${t.page?.slug ? `(/${t.page.slug})` : ''}`,
                                          value: t.page?.slug || t.slug || t.code,
                                          topicName: t.name
                                        }))}
                                        onChange={(val, option) => {
                                          const comparisons = pageForm.getFieldValue('comparisons') || [];
                                          comparisons[field.name] = {
                                            ...comparisons[field.name],
                                            slug: option.value,
                                            name: option.topicName
                                          };
                                          pageForm.setFieldsValue({ comparisons: comparisons });
                                        }}
                                      />
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-6"><Form.Item label="Alternative Skill Name" name={[field.name, "name"]}><Input placeholder="e.g. Kotlin" /></Form.Item></div>
                                </div>
                                <Form.Item label="Why Choose This?" name={[field.name, "whyChooseThis"]} style={{ marginBottom: 8 }}><Input.TextArea rows={2} placeholder="Strengths and ideal scenarios for this skill..." /></Form.Item>
                                <Form.Item label="Why Choose Alternative?" name={[field.name, "whyChooseAlternative"]} style={{ marginBottom: 0 }}><Input.TextArea rows={2} placeholder="Scenarios where the alternative skill is preferable..." /></Form.Item>
                              </Card>
                            ))}
                            <Button type="dashed" size="large" block onClick={() => add()}>Add Comparison</Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>
                  </div>
                </Tabs.TabPane>

                {/* TAB 3: Guidance */}
                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Guidance</span>} key="3" forceRender>
                  <div style={{ paddingTop: 16 }}>
                    <Card
                      size="small"
                      style={{ marginBottom: 20, borderRadius: 10, borderColor: "#e2e8f0", background: "#ffffff" }}
                      title={<span style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>✍️ Editorial Note & Industry Perspective</span>}
                      extra={
                        <Space>
                          <Tooltip title="Exa Search (Auto-fill Guidance only)">
                            <Button
                              icon={<GlobalOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => handleRunExaSearchDirect("guidance")}
                              loading={runningExa.guidance}
                            />
                          </Tooltip>
                          <Tooltip title="Exa Search Settings">
                            <Button
                              icon={<SettingOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => openExaConfig("guidance")}
                            />
                          </Tooltip>
                          <Tooltip title="Cloudflare AI (Creative)">
                            <Button
                              icon={<SettingOutlined />}
                              onClick={() => openConfig("guidance")}
                            />
                          </Tooltip>
                          <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            onClick={handleGenerateEnhancedGuidance}
                            loading={generatingSection.guidance}
                            style={{
                              background: '#8b5cf6',
                              borderColor: '#8b5cf6',
                              borderRadius: 4
                            }}
                          >
                            Generate Guidance
                          </Button>
                          <Form.Item name="hideEditorial" valuePropName="checked" noStyle>
                            <Switch
                              checkedChildren="Editorial Hidden"
                              unCheckedChildren="Editorial Visible"
                              style={{ background: pageForm.getFieldValue("hideEditorial") ? '#ef4444' : '#22c55e' }}
                            />
                          </Form.Item>
                        </Space>
                      }
                    >
                      <div className="row" style={{ marginBottom: 12 }}>
                        <div className="col-md-6">
                          <Form.Item label="Editorial Section Title" name="editorialTitle" extra="Heading displayed above Editorial Note (defaults to 'Editorial Note' if empty)." style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Editorial Note & Industry Perspective" />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="Editorial Section Subtitle" name="editorialSubtitle" extra="Subtitle displayed under Editorial section title" style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Independent analysis, key trade-offs, and practical takeaways." />
                          </Form.Item>
                        </div>
                      </div>

                      <Form.Item name="editorialNote" style={{ marginBottom: 0 }}>
                        <TipTapEditor editable={isEditable} placeholder="Write the editorial note, expert analysis, formatted lists, blockquotes, or links..." height="320px" />
                      </Form.Item>
                    </Card>

                    <Card
                      size="small"
                      style={{ marginBottom: 20, borderRadius: 10, borderColor: '#e2e8f0', background: '#ffffff' }}
                      title={
                        <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                          🧠 Expert Guidance & Mentor Intervention
                        </span>
                      }
                    >
                      <div className="row">
                        <div className="col-md-6">
                          <Form.Item label="Select Verified Mentor" name={["expertMentor", "mentorId"]} extra="Select from registered mentors in database to auto-populate profile details">
                            <MentorSelect
                              onChange={(mentorId, mentorObj) => {
                                pageForm.setFieldsValue({
                                  expertMentor: {
                                    mentorId: mentorId || "",
                                    name: mentorObj?.name || pageForm.getFieldValue(["expertMentor", "name"]) || "",
                                    title: mentorObj?.designation || pageForm.getFieldValue(["expertMentor", "title"]) || "",
                                    company: mentorObj?.organization || pageForm.getFieldValue(["expertMentor", "company"]) || "",
                                    image: mentorObj?.profileImage || pageForm.getFieldValue(["expertMentor", "image"]) || "",
                                    linkedinUrl: mentorObj?.linkedinUrl || pageForm.getFieldValue(["expertMentor", "linkedinUrl"]) || "",
                                    badgeText: pageForm.getFieldValue(["expertMentor", "badgeText"]) || "EXPERT ADVICE",
                                    advice: pageForm.getFieldValue(["expertMentor", "advice"]) || mentorObj?.headline || mentorObj?.bio || "",
                                  }
                                });
                              }}
                            />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="Badge Text" name={["expertMentor", "badgeText"]} extra="Defaults to 'EXPERT ADVICE' if left empty">
                            <Input placeholder="e.g. EXPERT ADVICE, MENTOR INSIGHT, PRO TIP" />
                          </Form.Item>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-4">
                          <Form.Item label="Mentor Name" name={["expertMentor", "name"]}>
                            <Input placeholder="e.g. Sarah Chen" />
                          </Form.Item>
                        </div>
                        <div className="col-md-4">
                          <Form.Item label="Designation / Role" name={["expertMentor", "title"]}>
                            <Input placeholder="e.g. Principal Software Architect" />
                          </Form.Item>
                        </div>
                        <div className="col-md-4">
                          <Form.Item label="Company / Organization" name={["expertMentor", "company"]}>
                            <Input placeholder="e.g. Google" />
                          </Form.Item>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <Form.Item label="Avatar / Profile Image URL" name={["expertMentor", "image"]} extra="Direct link to a square or circular profile photo">
                            <Input placeholder="https://..." />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="LinkedIn Profile URL" name={["expertMentor", "linkedinUrl"]}>
                            <Input placeholder="https://linkedin.com/in/..." />
                          </Form.Item>
                        </div>
                      </div>

                      <Form.Item label="Expert Advice / Guidance Quote" name={["expertMentor", "advice"]} extra="Core insight, recommendation, or actionable takeaway for learners" style={{ marginBottom: 16 }}>
                        <Input.TextArea rows={4} autoSize={{ minRows: 3, maxRows: 8 }} placeholder="e.g. Focus deeply on asynchronous primitives and system design before diving into high-level abstractions..." />
                      </Form.Item>

                      <Form.Item label="Common Pitfall / Mistake (Optional)" name="commonMistake" extra="The #1 concrete technical pitfall, anti-pattern, or misconception learners make." style={{ marginBottom: 16 }}>
                        <Input.TextArea rows={3} autoSize={{ minRows: 2, maxRows: 6 }} placeholder="e.g. Treating Java development as a syntax-first exercise, ignoring dependency management, framework behaviors, and JVM complexities..." />
                      </Form.Item>

                      <Form.Item label="Pro Rectification / Fix (Optional)" name="rectification" extra="The actionable pro fix or recommended engineering strategy to overcome this pitfall." style={{ marginBottom: 0 }}>
                        <Input.TextArea rows={3} autoSize={{ minRows: 2, maxRows: 6 }} placeholder="e.g. Stop learning Java in a vacuum and start navigating the ecosystem with a defensive engineering mindset. Use the latest LTS version, master memory management..." />
                      </Form.Item>
                    </Card>
                  </div>
                </Tabs.TabPane>

                {/* TAB 4: Careers */}
                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Careers</span>} key="4" forceRender>
                  <div style={{ paddingTop: 16 }}>
                    <Card
                      size="small"
                      style={{ marginBottom: 16, borderRadius: 10, borderColor: "#e2e8f0" }}
                      title={<span style={{ fontWeight: 700, fontSize: 15 }}>Careers Section Settings</span>}
                      extra={
                        <Form.Item name="hideCareerOutcomes" valuePropName="checked" noStyle>
                          <Switch
                            checkedChildren="Careers Hidden"
                            unCheckedChildren="Careers Visible"
                            style={{ background: pageForm.getFieldValue("hideCareerOutcomes") ? '#ef4444' : '#22c55e' }}
                          />
                        </Form.Item>
                      }
                    >
                      <div className="row">
                        <div className="col-md-6">
                          <Form.Item label="Careers Section Title" name="careersTitle" extra="Heading displayed above Career Outcomes section (defaults to 'Career Pathways & Industry Demand' if empty)." style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. High-Demand Career Paths in Java" />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="Careers Section Subtitle" name="careersSubtitle" extra="Subtitle displayed under Career Outcomes section title" style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Explore typical roles, salary trajectories, and industry demand." />
                          </Form.Item>
                        </div>
                      </div>
                    </Card>
                    {/* 🏆 Subsection A: Industry Certifications & Credentials */}
                    <Card
                      size="small"
                      title={
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontWeight: 800, fontSize: 16, color: "#1e1b4b" }}>
                            🏆 Subsection A: Industry Certifications & Credentials
                          </span>
                        </div>
                      }
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: '#6366f1', background: '#f8faff' }}
                      extra={
                        <Space>
                          <Button
                            type="dashed"
                            icon={<ThunderboltOutlined />}
                            onClick={handleAutoMatchCertifications}
                            loading={loadingItCertCourses}
                            style={{ color: "#6366f1", borderColor: "#c7d2fe" }}
                          >
                            Auto-Match IT Certs
                          </Button>
                          <Form.Item name="hideCertifications" valuePropName="checked" noStyle>
                            <Switch
                              checkedChildren="Certs Hidden"
                              unCheckedChildren="Certs Visible"
                              style={{ background: pageForm.getFieldValue("hideCertifications") ? '#ef4444' : '#22c55e' }}
                            />
                          </Form.Item>
                        </Space>
                      }
                    >
                      <div className="row" style={{ marginBottom: 16 }}>
                        <div className="col-md-6">
                          <Form.Item label="Section Title" name="certificationsTitle" extra="Heading displayed above Certifications (defaults to 'Certifications & Certificate Programs')." style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Java Certifications & Certificate Programs" />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="Section Subtitle" name="certificationsSubtitle" extra="Subtitle displayed under title" style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Explore industry certification exams, exam prep programs, and professional certificates." />
                          </Form.Item>
                        </div>
                      </div>

                      <Divider orientation="left" style={{ margin: "16px 0 12px", fontSize: 14, fontWeight: 700, color: "#4338ca" }}>
                        🏆 Structured Industry Credentials & Exam Prep
                      </Divider>

                      <Form.List name={["certificationsData", "industryCertifications"]}>
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card
                                key={field.key}
                                size="small"
                                style={{
                                  marginBottom: 16,
                                  borderRadius: 8,
                                  borderColor: "#c7d2fe",
                                  background: "#ffffff",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                                }}
                                title={
                                  <span style={{ fontWeight: 700, color: "#3730a3" }}>
                                    Credential #{field.name + 1}
                                  </span>
                                }
                                extra={
                                  <Button danger type="text" onClick={() => remove(field.name)}>
                                    Remove
                                  </Button>
                                }
                              >
                                <div className="row">
                                  <div className="col-md-5">
                                    <Form.Item
                                      label="Certification Name"
                                      name={[field.name, "name"]}
                                      rules={[{ required: true, message: "Certification name is required" }]}
                                    >
                                      <Input placeholder="e.g. Oracle Certified Professional: Java SE 21 Developer" />
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-3">
                                    <Form.Item label="Issuer / Vendor" name={[field.name, "issuer"]}>
                                      <Input placeholder="e.g. Oracle, AWS, Linux Foundation" />
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-2">
                                    <Form.Item label="Level" name={[field.name, "level"]}>
                                      <Select
                                        options={[
                                          { label: "Foundations", value: "Foundations" },
                                          { label: "Associate", value: "Associate" },
                                          { label: "Professional", value: "Professional" },
                                          { label: "Specialist", value: "Specialist" },
                                          { label: "Expert", value: "Expert" },
                                        ]}
                                      />
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-2">
                                    <Form.Item label="Exam Code" name={[field.name, "examCode"]}>
                                      <Input placeholder="e.g. 1Z0-830" />
                                    </Form.Item>
                                  </div>
                                </div>

                                <div className="row">
                                  <div className="col-md-6">
                                    <Form.Item label="Best For (Target Audience)" name={[field.name, "bestFor"]}>
                                      <Input placeholder="e.g. Enterprise Java Developers & Microservices Engineers" />
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-6">
                                    <Form.Item label="Official Exam URL" name={[field.name, "url"]}>
                                      <Input placeholder="https://education.oracle.com/..." />
                                    </Form.Item>
                                  </div>
                                </div>

                                <Form.Item
                                  label="Guidance & Editorial Takeaway"
                                  name={[field.name, "guidance"]}
                                  extra="A short summary explaining when to choose this credential and why it matters."
                                >
                                  <Input.TextArea
                                    rows={2}
                                    placeholder="e.g. The gold standard for validating enterprise-grade Java proficiency. Essential for senior backend engineering roles."
                                  />
                                </Form.Item>

                                <div
                                  style={{
                                    marginTop: 12,
                                    padding: "12px 14px",
                                    background: "#f8fafc",
                                    borderRadius: 6,
                                    border: "1px dashed #cbd5e1",
                                  }}
                                >
                                  <div style={{ fontWeight: 600, fontSize: 13, color: "#334155", marginBottom: 8 }}>
                                    📚 Linked Preparation Courses for this Exam:
                                  </div>
                                  <div className="row">
                                    <div className="col-md-7">
                                      <Form.Item
                                        label="Specific Prep Courses (from catalog)"
                                        name={[field.name, "prepCourses"]}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <DebouncedCourseSelect placeholder="Search and link prep courses for this exam..." />
                                      </Form.Item>
                                    </div>
                                    <div className="col-md-5">
                                      <Form.Item
                                        label="Linked Course Collection / Bundle"
                                        name={[field.name, "prepCollection"]}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <Select
                                          placeholder="Select prep bundle..."
                                          allowClear
                                          options={courseCollections.map((c) => ({
                                            label: `${c.name} (${c.provider || c.platform || "Bundle"})`,
                                            value: c._id,
                                          }))}
                                          filterOption={(input, option) =>
                                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                          }
                                        />
                                      </Form.Item>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            ))}
                            <Button
                              type="dashed"
                              block
                              icon={<PlusOutlined />}
                              onClick={() => add({ level: "Professional" })}
                              style={{ borderColor: "#6366f1", color: "#6366f1", fontWeight: 600 }}
                            >
                              + Add Industry Certification Credential
                            </Button>
                          </div>
                        )}
                      </Form.List>

                      <Divider style={{ margin: "20px 0 14px" }} />

                      <div style={{ background: "#ffffff", padding: 14, borderRadius: 8, border: "1px solid #e2e8f0" }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#475569", marginBottom: 6 }}>
                          Fallback / General IT Certification Courses (Legacy Slider Support):
                        </div>
                        <Form.Item
                          name="certificationCourses"
                          help="Courses assigned here will render in the legacy course slider if no structured credentials exist."
                          style={{ marginBottom: 0 }}
                        >
                          <DebouncedCourseSelect placeholder="Search and assign general IT certification courses..." />
                        </Form.Item>
                        <Form.Item shouldUpdate={(prev, curr) => prev.certificationCourses !== curr.certificationCourses} noStyle>
                          {() => <CertificationCardsPreview pageForm={pageForm} itCertCourses={itCertCourses} />}
                        </Form.Item>
                      </div>
                    </Card>

                    {/* Career Outcomes & Roles */}
                    <Card
                      size="small"
                      title={
                        <span style={{ fontWeight: 700, fontSize: 16 }}>
                          🎓 Subsection B: Career Outcomes & Certificate Programs
                        </span>
                      }
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                      extra={
                        <Space>
                          <Button 
                            type="primary" 
                            icon={<ThunderboltOutlined />} 
                            size="small"
                            onClick={handleAutoMatchAllDomains}
                            style={{ background: "#8b5cf6", borderColor: "#8b5cf6" }}
                          >
                            Auto-Match All Domains
                          </Button>
                          <Tooltip title="Exa Search (Auto-fill Careers only)">
                            <Button
                              icon={<GlobalOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => handleRunExaSearchDirect("careers")}
                              loading={runningExa.careers}
                            />
                          </Tooltip>
                          <Tooltip title="Exa Search Settings">
                            <Button
                              icon={<SettingOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => openExaConfig("careers")}
                            />
                          </Tooltip>
                          <Tooltip title="Cloudflare AI (Creative)">
                            <Button
                              icon={<SettingOutlined />}
                              onClick={() => openConfig("careers")}
                            />
                          </Tooltip>
                          <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            loading={generatingSection.careers}
                            onClick={() => handleGenerateSection("careers")}
                            style={{
                              background: '#8b5cf6',
                              borderColor: '#8b5cf6',
                              borderRadius: 4
                            }}
                          >
                            Generate Careers
                          </Button>
                        </Space>
                      }
                    >
                      <Form.List name="careerOutcomes">
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card key={field.key} size="small" style={{ marginBottom: 16, borderRadius: 8, background: '#fafafa' }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                                <div className="row">
                                  <div className="col-md-4"><Form.Item label="Role" name={[field.name, "role"]}><Input /></Form.Item></div>
                                  <div className="col-md-4"><Form.Item label="Trend / Demand" name={[field.name, "trend"]}><Input /></Form.Item></div>
                                  <div className="col-md-4"><Form.Item label="Salary (Global Average / Estimate)" name={[field.name, "salary"]}><Input placeholder="e.g. $110,000 / yr" /></Form.Item></div>
                                </div>
                                <Form.Item label="Description" name={[field.name, "description"]}><Input.TextArea rows={2} /></Form.Item>
                                <div className="row">
                                  <div className="col-md-4"><Form.Item label="Top Skills Required" name={[field.name, "keySkills"]}><Select mode="tags" style={{ width: '100%' }} placeholder="Select or type skills" /></Form.Item></div>
                                  <div className="col-md-4"><Form.Item label="Target Certifications" name={[field.name, "certifications"]}><Select mode="tags" style={{ width: '100%' }} placeholder="Select or type certs" /></Form.Item></div>
                                  <div className="col-md-4"><Form.Item label="Hiring Companies" name={[field.name, "companies"]}><Select mode="tags" style={{ width: '100%' }} placeholder="Select or type companies" /></Form.Item></div>
                                </div>

                                {/* Certificate Programs / Collections */}
                                <div style={{ marginTop: 12, padding: '12px 14px', background: 'rgba(99, 102, 241, 0.04)', borderRadius: 6, border: '1px solid rgba(99, 102, 241, 0.18)' }}>
                                  <div style={{ fontWeight: 700, fontSize: 13, color: '#3730a3', marginBottom: 6 }}>
                                    🎓 Certificate Programs & Specializations (Course Collections)
                                  </div>
                                  <Form.Item
                                    name={[field.name, "certificatePrograms"]}
                                    extra="Assign multi-course certificate programs or curated specializations that prepare learners for this role."
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Select
                                      mode="multiple"
                                      placeholder="Select course collections / certificate programs for this career role..."
                                      options={courseCollections.map((c) => ({
                                        label: `${c.name} (${c.provider || c.platform || "Program"} · ${c.duration || "Self-paced"})`,
                                        value: c._id,
                                      }))}
                                      filterOption={(input, option) =>
                                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                      }
                                    />
                                  </Form.Item>
                                </div>

                                <div style={{ marginTop: 12, padding: '12px', background: 'rgba(139, 92, 246, 0.04)', borderRadius: 6, border: '1px solid rgba(139, 92, 246, 0.1)' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                    <span style={{ fontWeight: 600, fontSize: 13, color: '#4c1d95' }}>Domain Certifications (IT Courses)</span>
                                    <Button 
                                      type="dashed" 
                                      icon={<ThunderboltOutlined />} 
                                      size="small"
                                      onClick={() => handleAutoMatchDomainCertifications(field.name)}
                                      loading={loadingDomainCerts[field.name]}
                                      style={{ color: "#7c3aed", borderColor: "#c4b5fd", background: "white" }}
                                    >
                                      Auto-Match
                                    </Button>
                                  </div>
                                  <Form.Item name={[field.name, "certificationCourses"]} style={{ marginBottom: 0 }}>
                                    <DebouncedCourseSelect placeholder="Search or select IT Certification Courses for this role..." />
                                  </Form.Item>
                                </div>
                              </Card>
                            ))}
                            <Button type="dashed" size="large" block onClick={() => add()}>Add Career Outcome</Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>

                    {/* Regional Salary Insights */}
                    <Card
                      size="small"
                      title={
                        <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
                          💰 Regional Salary Insights (US, Europe, India)
                        </span>
                      }
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                      extra={
                        <Space>
                          <Tooltip title="Exa Search (Auto-fill accurate 2025/2026 regional salaries from Glassdoor, Levels.fyi, AmbitionBox)">
                            <Button
                              icon={<GlobalOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => handleRunExaSearchDirect("salaryInsights")}
                              loading={runningExa.salaryInsights}
                            />
                          </Tooltip>
                          <Tooltip title="Exa Search Settings">
                            <Button
                              icon={<SettingOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => openExaConfig("salaryInsights")}
                            />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Form.List name="salaryInsights">
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card 
                                key={field.key} 
                                size="small" 
                                style={{ marginBottom: 16, borderRadius: 8, background: '#f8fafc', borderColor: '#e2e8f0' }} 
                                extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}
                              >
                                <div className="row">
                                  <div className="col-md-3">
                                    <Form.Item label="Region" name={[field.name, "region"]} rules={[{ required: true, message: "Region is required" }]}>
                                      <Select placeholder="Select Region">
                                        <Select.Option value="United States">🇺🇸 United States (USD)</Select.Option>
                                        <Select.Option value="Europe">🇪🇺 Europe (EUR)</Select.Option>
                                        <Select.Option value="India">🇮🇳 India (INR)</Select.Option>
                                      </Select>
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-3">
                                    <Form.Item label="Currency Code" name={[field.name, "currency"]} rules={[{ required: true, message: "Currency is required" }]}>
                                      <Select placeholder="Currency">
                                        <Select.Option value="USD">USD ($)</Select.Option>
                                        <Select.Option value="EUR">EUR (€)</Select.Option>
                                        <Select.Option value="INR">INR (₹)</Select.Option>
                                      </Select>
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-6">
                                    <Form.Item label="Benchmark Source" name={[field.name, "source"]}>
                                      <Input placeholder="e.g. Levels.fyi & Glassdoor (2026)" />
                                    </Form.Item>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-4">
                                    <Form.Item label="Entry / Minimum Annual Salary" name={[field.name, "min"]} rules={[{ required: true, message: "Min salary is required" }]}>
                                      <InputNumber style={{ width: '100%' }} placeholder="e.g. 75000 or 600000" />
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-4">
                                    <Form.Item label="Median / Average Annual Salary" name={[field.name, "median"]} rules={[{ required: true, message: "Median salary is required" }]}>
                                      <InputNumber style={{ width: '100%' }} placeholder="e.g. 120000 or 1800000" />
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-4">
                                    <Form.Item label="Senior / Maximum Annual Salary" name={[field.name, "max"]} rules={[{ required: true, message: "Max salary is required" }]}>
                                      <InputNumber style={{ width: '100%' }} placeholder="e.g. 185000 or 3500000" />
                                    </Form.Item>
                                  </div>
                                </div>
                              </Card>
                            ))}
                            <Button type="dashed" size="large" block onClick={() => add()}>Add Regional Salary Benchmark</Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>
                  </div>
                </Tabs.TabPane>

                {/* TAB 5: Roadmap */}
                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Roadmap</span>} key="5" forceRender>
                  <div style={{ paddingTop: 16 }}>
                    <Card
                      size="small"
                      style={{ marginBottom: 16, borderRadius: 10, borderColor: "#e2e8f0" }}
                      title={<span style={{ fontWeight: 700, fontSize: 15 }}>Roadmap Section Settings</span>}
                      extra={
                        <Space>
                          <Form.Item name="hideRoadmap" valuePropName="checked" noStyle>
                            <Switch
                              checkedChildren="Roadmap Hidden"
                              unCheckedChildren="Roadmap Visible"
                              onChange={(checked) => {
                                pageForm.setFieldsValue({
                                  hideRoadmap: checked,
                                  hideLearningPaths: checked
                                });
                              }}
                              style={{ background: pageForm.getFieldValue("hideRoadmap") || pageForm.getFieldValue("hideLearningPaths") ? '#ef4444' : '#22c55e' }}
                            />
                          </Form.Item>
                          <Form.Item name="hideLearningPaths" valuePropName="checked" noStyle hidden>
                            <Input />
                          </Form.Item>
                          <Form.Item name="hideCertifications" valuePropName="checked" noStyle>
                            <Switch
                              checkedChildren="Certifications Hidden"
                              unCheckedChildren="Certifications Visible"
                              style={{ background: pageForm.getFieldValue("hideCertifications") ? '#ef4444' : '#22c55e' }}
                            />
                          </Form.Item>
                        </Space>
                      }
                    >
                      <div className="row">
                        <div className="col-md-6">
                          <Form.Item label="Roadmap Section Title" name="roadmapTitle" extra="Heading displayed above Roadmap section (defaults to 'Learning Roadmap' if empty)." style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Java Learning Roadmap & Skill Progression" />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="Roadmap Section Subtitle" name="roadmapSubtitle" extra="Subtitle displayed under Learning Roadmap section title" style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. A structured path from fundamental concepts to job-ready expertise." />
                          </Form.Item>
                        </div>
                      </div>
                    </Card>
                    
                    <Card
                      size="small"
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>⭐ Recommended Courses (Showcase)</span>}
                      style={{ marginBottom: 16, borderRadius: 10, borderColor: '#fbbf24', background: '#fffbeb' }}
                      extra={<Tag color="gold">Displayed after roadmap stages</Tag>}
                    >
                      <Form.Item
                        label="Assign Best-Selling Courses"
                        name="recommendedCourses"
                        help="These courses are showcased prominently after the stage-wise roadmap section on the public page. No limit."
                        style={{ marginBottom: 0 }}
                      >
                        <DebouncedCourseSelect placeholder="Search and assign recommended courses..." />
                      </Form.Item>
                    </Card>

                    <Card
                      size="small"
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Roadmap Stages</span>}
                      style={{ borderRadius: 10, borderColor: "#e2e8f0" }}
                      extra={
                        <Space>
                          <Tooltip title="Exa Search (Auto-fill roadmap)">
                            <Button
                              icon={<GlobalOutlined style={{ color: "#1890ff" }} />}
                              onClick={handleRoadmapExaSearch}
                              loading={runningExa.roadmap}
                            />
                          </Tooltip>
                          <Tooltip title="Exa Search Settings">
                            <Button
                              icon={<SettingOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => openExaConfig("roadmap")}
                            />
                          </Tooltip>
                        </Space>
                      }
                    >
                      {(editingPage?.stages || []).map((stage, index) => (
                        <Card
                          key={stage.level}
                          size="small"
                          title={`${STAGE_LEVEL_LABELS[stage.level]} Stage`}
                          style={{
                            marginBottom: index < 2 ? 16 : 0,
                            borderRadius: 8,
                            borderColor: pageForm.getFieldValue(["stages", index, "isHidden"]) ? '#fecaca' : '#f1f5f9',
                            background: '#fafafa',
                            opacity: pageForm.getFieldValue(["stages", index, "isHidden"]) ? 0.6 : 1,
                            transition: 'all 0.3s'
                          }}
                          extra={
                            <Space>
                              <Form.Item name={["stages", index, "enableApiFallback"]} valuePropName="checked" noStyle>
                                <Checkbox style={{ fontWeight: 600, color: '#475569' }}>Enable API Fallback?</Checkbox>
                              </Form.Item>
                              <Form.Item name={["stages", index, "isHidden"]} valuePropName="checked" noStyle>
                                <Switch checkedChildren="Hidden" unCheckedChildren="Visible" style={{ background: pageForm.getFieldValue(["stages", index, "isHidden"]) ? '#ef4444' : '#22c55e' }} />
                              </Form.Item>
                              <Tag color="blue" style={{ margin: 0 }}>{stage.level}</Tag>
                            </Space>
                          }
                        >
                          <div className="row">
                            <div className="col-md-4"><Form.Item label="Title" name={["stages", index, "title"]}><Input /></Form.Item></div>
                            <div className="col-md-4"><Form.Item label="Subtitle" name={["stages", index, "subtitle"]}><Input /></Form.Item></div>
                            <div className="col-md-4"><Form.Item label="Roadmap Step" name={["stages", index, "roadmapStep"]}><Input /></Form.Item></div>
                          </div>
                          <Form.Item label="Description" name={["stages", index, "description"]}><Input.TextArea rows={2} /></Form.Item>
                          <div className="row">
                            <div className="col-md-4"><Form.Item label="Learning Objective" name={["stages", index, "learningObjective"]}><Input /></Form.Item></div>
                            <div className="col-md-4"><Form.Item label="Expected Outcome" name={["stages", index, "expectedOutcome"]}><Input /></Form.Item></div>
                            <div className="col-md-4"><Form.Item label="Estimated Duration" name={["stages", index, "estimatedDuration"]}><Input /></Form.Item></div>
                          </div>
                          <Form.Item label="Learning Intent" name={["stages", index, "learningIntent"]} style={{ marginBottom: 16 }}><Input.TextArea rows={2} /></Form.Item>
                          <Form.Item 
                            label="Assigned Top Courses" 
                            name={["stages", index, "assignedCourses"]} 
                            style={{ marginBottom: 0 }}
                            help="Search and assign up to 10 top courses to highlight for this stage."
                          >
                            <DebouncedCourseSelect maxCount={10} />
                          </Form.Item>
                        </Card>
                      ))}
                    </Card>


                  </div>
                </Tabs.TabPane>

                {/* TAB 6: FAQ & Resources */}
                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>FAQ & Resources</span>} key="6" forceRender>
                  <div style={{ paddingTop: 16 }}>
                    <Card
                      size="small"
                      style={{ marginBottom: 16, borderRadius: 10, borderColor: "#e2e8f0" }}
                      title={<span style={{ fontWeight: 700, fontSize: 15 }}>FAQ Section Settings</span>}
                      extra={
                        <Form.Item name="hideFaq" valuePropName="checked" noStyle>
                          <Switch
                            checkedChildren="FAQ Hidden"
                            unCheckedChildren="FAQ Visible"
                            style={{ background: pageForm.getFieldValue("hideFaq") ? '#ef4444' : '#22c55e' }}
                          />
                        </Form.Item>
                      }
                    >
                      <div className="row">
                        <div className="col-md-6">
                          <Form.Item label="FAQ Section Title" name="faqTitle" extra="Heading displayed above FAQ section (defaults to 'Frequently Asked Questions' if empty)." style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Frequently Asked Questions About Java" />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="FAQ Section Subtitle" name="faqSubtitle" extra="Subtitle displayed under FAQ section title" style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Everything you need to know about starting your career." />
                          </Form.Item>
                        </div>
                      </div>
                    </Card>
                    <Card
                      size="small"
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>FAQ</span>}
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                      extra={
                        <Space>

                          <Tooltip title="Exa Search Settings">
                            <Button
                              icon={<SettingOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => openExaConfig("faq")}
                            />
                          </Tooltip>
                          <Tooltip title="Cloudflare AI (Creative)">
                            <Button
                              icon={<SettingOutlined />}
                              onClick={() => openConfig("faq")}
                            />
                          </Tooltip>
                          <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            onClick={handleGenerateEnhancedFaq}
                            loading={generatingSection.faq}
                            style={{
                              background: '#8b5cf6',
                              borderColor: '#8b5cf6',
                              borderRadius: 4
                            }}
                          >
                            Generate FAQs
                          </Button>
                        </Space>
                      }
                    >
                      <Form.List name="faq">
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card key={field.key} size="small" style={{ marginBottom: 12, borderRadius: 8, background: '#fafafa' }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                                <div className="row">
                                  <div className="col-md-9">
                                    <Form.Item label="Question" name={[field.name, "question"]}><Input /></Form.Item>
                                  </div>
                                  <div className="col-md-3">
                                    <Form.Item label="Display Rank (1 = Top)" name={[field.name, "priority"]} extra="1 = 1st FAQ, 2 = 2nd FAQ">
                                      <InputNumber min={1} style={{ width: '100%' }} placeholder="1" />
                                    </Form.Item>
                                  </div>
                                </div>
                                <Form.Item label="Answer (Rich Text)" name={[field.name, "answer"]} style={{ marginBottom: 0 }}>
                                  <TipTapEditor editable={isEditable} placeholder="Write the detailed FAQ answer, bullet points, links, and bold highlights..." minHeight="120px" height="170px" />
                                </Form.Item>
                              </Card>
                            ))}
                            <Button type="dashed" size="large" block onClick={() => add()}>Add FAQ</Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>

                    <Card size="small" style={{ marginBottom: 16, borderRadius: 10, borderColor: "#e2e8f0" }}>
                      <div className="row">
                        <div className="col-md-6">
                          <Form.Item label="Resources Section Title" name="resourcesTitle" extra="Heading displayed above Resources section (defaults to 'Helpful Resources' if empty)." style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Helpful Resources & Links" />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="Resources Section Subtitle" name="resourcesSubtitle" extra="Subtitle displayed under Resources section title" style={{ marginBottom: 0 }}>
                            <Input size="large" placeholder="e.g. Official documentation, communities, and tools." />
                          </Form.Item>
                        </div>
                      </div>
                    </Card>
                    <Card
                      size="small"
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Resources</span>}
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                    >
                      <Form.List name="resources">
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card key={field.key} size="small" style={{ marginBottom: 12, borderRadius: 8, background: '#fafafa' }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                                <div className="row">
                                  <div className="col-md-6">
                                    <Form.Item label="Resource Name" name={[field.name, "title"]}><Input placeholder="e.g. Official Documentation" /></Form.Item>
                                  </div>
                                  <div className="col-md-6">
                                    <Form.Item label="URL" name={[field.name, "url"]}><Input placeholder="https://..." /></Form.Item>
                                  </div>
                                </div>
                                <Form.Item label="Short Description" name={[field.name, "description"]} style={{ marginBottom: 0 }}><Input.TextArea rows={2} /></Form.Item>
                              </Card>
                            ))}
                            <Button type="dashed" size="large" block onClick={() => add()}>Add Resource</Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>
                  </div>
                </Tabs.TabPane>

                {/* TAB 7: Meta */}
                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Meta</span>} key="7" forceRender>
                  <div style={{ paddingTop: 16 }}>
                    <Card
                      size="small"
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Meta Metadata</span>}
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                      extra={
                        <Space>
                          <Tooltip title="Exa Search Settings">
                            <Button
                              icon={<SettingOutlined style={{ color: "#1890ff" }} />}
                              onClick={() => openExaConfig("seo")}
                            />
                          </Tooltip>
                          <Tooltip title="Cloudflare AI (Creative)">
                            <Button
                              icon={<SettingOutlined />}
                              onClick={() => openConfig("seo")}
                            />
                          </Tooltip>
                          <Button
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            loading={generatingSection.seo}
                            onClick={handleGenerateEnhancedSeo}
                            style={{
                              background: '#8b5cf6',
                              borderColor: '#8b5cf6',
                              border: 'none',
                              borderRadius: 4
                            }}
                          >
                            Generate Meta
                          </Button>
                        </Space>
                      }
                    >
                      <div className="row">
                        <div className="col-md-4">
                          <Form.Item label="Slug / URL Path" name="slug" rules={[{ required: true, message: "Slug is required" }]}>
                            <Input size="large" disabled={editingPage?.status !== "draft"} />
                          </Form.Item>
                        </div>
                        <div className="col-md-4">
                          <Form.Item label="Primary Keyword" name={["seo", "primaryKeyword"]} rules={[{ required: true, message: "Primary keyword is required" }]}>
                            <Input size="large" />
                          </Form.Item>
                        </div>
                        <div className="col-md-4">
                          <Form.Item label="Title Suffix" name={["seo", "titleSuffix"]}>
                            <Input size="large" />
                          </Form.Item>
                        </div>
                      </div>
                      <Form.Item label="Short Description (Meta)" name={["seo", "shortDescription"]} rules={[{ required: true, message: "Short description is required" }]}>
                        <Input.TextArea rows={3} />
                      </Form.Item>
                      <Form.Item label="Keywords">
                        <StringList name={["seo", "keywords"]} addLabel="Add Keyword" placeholder="Keyword" />
                      </Form.Item>

                      <Divider orientation="left" style={{ marginTop: 32 }}>OG Tags & Social Preview</Divider>
                      <div className="row">
                        <div className="col-md-6">
                          <Form.Item label="OG Title" name={["seo", "ogTitle"]}>
                            <Input size="large" placeholder="Open Graph Title" />
                          </Form.Item>
                        </div>
                        <div className="col-md-6">
                          <Form.Item label="OG Description" name={["seo", "ogDescription"]}>
                            <Input size="large" placeholder="Open Graph Description" />
                          </Form.Item>
                        </div>
                      </div>

                      {editingPage?.ogImage && (
                        <div style={{ marginBottom: 16 }}>
                          <img
                            src={editingPage.ogImage}
                            alt="OG Preview"
                            style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, border: "1px solid #e2e8f0" }}
                          />
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 24 }}>
                        <Upload
                          accept="image/jpeg,image/png,image/webp"
                          maxCount={1}
                          beforeUpload={(file) => {
                            handleOgImageUpload(file);
                            return false;
                          }}
                          showUploadList={false}
                        >
                          <Button icon={<UploadOutlined />}>
                            {editingPage?.ogImage || pageForm.getFieldValue("ogImage") ? "Replace OG Image" : "Upload OG Image"}
                          </Button>
                        </Upload>
                        {(editingPage?.ogImage || pageForm.getFieldValue("ogImage")) && (
                          <Button danger type="text" onClick={() => {
                            pageForm.setFieldsValue({ ogImage: "" });
                            setEditingPage(prev => ({ ...prev, ogImage: "" }));
                          }}>Remove OG Image</Button>
                        )}
                      </div>
                    </Card>
                  </div>
                </Tabs.TabPane>

              </Tabs>
            </Card>
          </Form>
        </div>

        {/* Modals & Config */}

        <AIGeneratorConfig
          visible={configModal.visible}
          onClose={() => setConfigModal(prev => ({ ...prev, visible: false }))}
          category={configModal.category}
          defaultPrompt={configModal.defaultPrompt}
          contextInstructions={configModal.contextInstructions}
          hidePromptsTab={configModal.hidePromptsTab}
        />

        <ExaSearchConfig
          visible={exaConfigModal.visible}
          onClose={() => setExaConfigModal(prev => ({ ...prev, visible: false }))}
          section={exaConfigModal.section}
          skillName={editingPage?.name}
          onApplyResults={(data) => handleExaResults(exaConfigModal.section, data)}
        />
      </div>
    </Page>
  );
};

export default SkillPageBuilder;
